import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../../utils/utils";

export default function NewNormalTaskCreation() {
  const API_URL = process.env.REACT_APP_API_URL;

  // Form State
  const [scaninfo, setScaninfo] = useState({
    task_file: null,
    task_creation_date: "",
    partner_id: "",
  });

  // Data State
  const [partners, setPartners] = useState([]);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Editing State
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [dataModified, setDataModified] = useState(false); // Track if data was edited

  // Results State
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("valid");

  // Batch History State
  const [mainView, setMainView] = useState("upload"); // "upload" | "history"
  const [batchHistory, setBatchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchDetailTab, setBatchDetailTab] = useState("all"); // "all" | "success" | "failed" | "errors"
  const [processingProgress, setProcessingProgress] = useState(null);
  const [isDragging, setIsDragging] = useState(false); // Drag & drop state
  const [lastHistoryUpdate, setLastHistoryUpdate] = useState(null); // Track last refresh timestamp
  
  // Date Filter State (default: last 7 days)
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  };
  const getDefaultToDate = () => new Date().toISOString().split('T')[0];
  
  const [historyFromDate, setHistoryFromDate] = useState(getDefaultFromDate());
  const [historyToDate, setHistoryToDate] = useState(getDefaultToDate());
  const [historyStatusFilter, setHistoryStatusFilter] = useState(""); // "" = all
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(50);
  const [historyTotalTasks, setHistoryTotalTasks] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  
  // Animation & Notification State
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDataHash, setUploadedDataHash] = useState(null); // Prevent duplicate uploads
  
  // View Mode State (for Valid/Invalid tabs)
  const [viewMode, setViewMode] = useState("table"); // "table" | "card"
  const [validRowsPage, setValidRowsPage] = useState(0);
  const [validRowsPageSize, setValidRowsPageSize] = useState(10);
  const [invalidRowsPage, setInvalidRowsPage] = useState(0);
  const [invalidRowsPageSize, setInvalidRowsPageSize] = useState(10);
  const [invalidViewMode, setInvalidViewMode] = useState("table"); // "table" | "card"
  
  // Upload Mode State
  const [uploadMode, setUploadMode] = useState("bulk"); // "bulk" | "manual"
  const [batchDescription, setBatchDescription] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem("normalTaskOnboardingSeen");
  });
  
  // Manual Order Entry State
  const [manualOrder, setManualOrder] = useState({
    opsReference: "",
    receiverName: "",
    receiverPhone: "",
    deliveryAddress: "",
    aptDetails: "",
    city: "",
    weight: "0.5",
    noOfItems: "1",
    codAmount: "0",
    itemDescription: "",
    slot: "1",
    bookingDate: new Date().toISOString().split('T')[0],
  });

  // Local storage key for batch history (fallback when API not available)
  const BATCH_HISTORY_KEY = "normalTaskBatchHistory";
  
  // Generate hash of data to prevent duplicate uploads
  const generateDataHash = (rows) => {
    const dataString = rows.map(r => 
      `${r["Ops Reference"]}-${r["Receiver's Name"]}-${r["Receiver's Mobile Number"]}`
    ).join("|");
    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
      const char = dataString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  };
  
  // Show centered notification
  const showNotification = (message, duration = 2500) => {
    setNotificationMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => {
      setShowSuccessNotification(false);
      setNotificationMessage("");
    }, duration);
  };

  // Styles
  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    title: {
      fontSize: "22px",
      fontWeight: "600",
      color: "#2d3748",
      margin: 0,
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "20px",
      border: "1px solid #e8ecf1",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
      margin: 0,
    },
    cardBody: {
      padding: "20px",
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      marginBottom: "0",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "500",
      color: "#4b5563",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "border-color 0.2s",
      outline: "none",
    },
    select: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      cursor: "pointer",
      outline: "none",
    },
    btnPrimary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#4f7df3",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    btnSecondary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#4b5563",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      cursor: "pointer",
    },
    btnSuccess: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#10b981",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
    },
    btnSmall: {
      padding: "6px 14px",
      fontSize: "13px",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "16px",
      marginBottom: "20px",
    },
    statCard: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "20px",
      textAlign: "center",
      border: "1px solid #e8ecf1",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    statValue: {
      fontSize: "28px",
      fontWeight: "700",
      marginBottom: "4px",
    },
    statLabel: {
      fontSize: "13px",
      color: "#6b7280",
    },
    tabs: {
      display: "flex",
      gap: "4px",
      padding: "4px",
      backgroundColor: "#f3f4f6",
      borderRadius: "10px",
      marginBottom: "20px",
    },
    tab: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    tabActive: {
      backgroundColor: "#fff",
      color: "#4f7df3",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    tabInactive: {
      backgroundColor: "transparent",
      color: "#6b7280",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px",
    },
    th: {
      padding: "12px 14px",
      textAlign: "left",
      fontWeight: "600",
      color: "#4b5563",
      backgroundColor: "#f9fafb",
      borderBottom: "1px solid #e5e7eb",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 14px",
      borderBottom: "1px solid #e5e7eb",
      color: "#374151",
    },
    alert: {
      padding: "14px 18px",
      borderRadius: "10px",
      marginBottom: "20px",
      fontSize: "14px",
    },
    alertError: {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
    },
    alertSuccess: {
      backgroundColor: "#f0fdf4",
      color: "#16a34a",
      border: "1px solid #bbf7d0",
    },
    toggleContainer: {
      display: "inline-flex",
      backgroundColor: "#f1f5f9",
      borderRadius: "14px",
      padding: "5px",
      marginBottom: "24px",
      marginLeft: "0",
      border: "1px solid #e2e8f0",
    },
    toggleBtn: {
      padding: "12px 28px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    toggleBtnActive: {
      backgroundColor: "#fff",
      color: "#1e40af",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    },
    toggleBtnInactive: {
      backgroundColor: "transparent",
      color: "#64748b",
    },
    badge: {
      display: "inline-block",
      padding: "4px 10px",
      fontSize: "12px",
      fontWeight: "500",
      borderRadius: "12px",
    },
    badgePending: { backgroundColor: "#fef3c7", color: "#92400e" },
    badgeQueued: { backgroundColor: "#e0e7ff", color: "#4338ca" },
    badgeProcessing: { backgroundColor: "#dbeafe", color: "#1d4ed8" },
    badgeCompleted: { backgroundColor: "#d1fae5", color: "#065f46" },
    badgeFailed: { backgroundColor: "#fee2e2", color: "#dc2626" },
    badgePaused: { backgroundColor: "#e5e7eb", color: "#374151" },
    progressBar: {
      width: "100%",
      height: "8px",
      backgroundColor: "#e5e7eb",
      borderRadius: "4px",
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      backgroundColor: "#4f7df3",
      borderRadius: "4px",
      transition: "width 0.3s",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "20px",
    },
    modalContent: {
      backgroundColor: "#fff",
      borderRadius: "16px",
      width: "100%",
      maxWidth: "900px",
      maxHeight: "85vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      margin: "auto",
    },
    modalHeader: {
      padding: "20px 24px",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderRadius: "16px 16px 0 0",
    },
    modalBody: {
      padding: "24px",
      overflowY: "auto",
      flex: 1,
      backgroundColor: "#fff",
    },
    // Notification overlay styles - appears from top
    notificationOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      paddingTop: "40px",
      zIndex: 10000,
      animation: "fadeIn 0.3s ease-out",
    },
    notificationCard: {
      backgroundColor: "#fff",
      borderRadius: "16px",
      padding: "20px 30px",
      textAlign: "center",
      boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.3)",
      animation: "slideDown 0.4s ease-out",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      minWidth: "320px",
    },
    notificationIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "50%",
      backgroundColor: "#d1fae5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      animation: "pulse 0.6s ease-out",
    },
    notificationText: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#1e293b",
      margin: 0,
      textAlign: "left",
    },
    // Loading spinner styles
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid #ffffff40",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    },
    btnUploading: {
      backgroundColor: "#6366f1",
      cursor: "not-allowed",
      opacity: 0.9,
    },
  };

  // CSS keyframes for animations (injected into document)
  React.useEffect(() => {
    const styleId = "normal-task-animations";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes slideDown {
          from { 
            opacity: 0; 
            transform: translateY(-100px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes slideIn {
          from { 
            opacity: 0; 
            transform: scale(0.8) translateY(-20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        @keyframes pulse {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes slideDownAlert {
          from { 
            opacity: 0; 
            transform: translateY(-30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes slideUpAlert {
          from { 
            opacity: 1; 
            transform: translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: translateY(-30px); 
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Auto-clear error messages after 8 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-clear success messages after 5 seconds
  React.useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Get user info from session
  const getUserId = () => sessionStorage.getItem("userid") || "1";
  const getUserName = () => sessionStorage.getItem("username") || sessionStorage.getItem("name") || "System";
  const getUserEmail = () => sessionStorage.getItem("useremail") || "";

  // Fetch batch history - fetches all batches with date filtering and pagination
  const fetchBatchHistory = useCallback(async (fromDate = historyFromDate, toDate = historyToDate, status = historyStatusFilter, page = historyPage, size = historyPageSize) => {
    setLoadingHistory(true);
    setError("");
    
    try {
      // Build API URL with query parameters
      const userId = getUserId();
      let apiUrl = `${API_URL}shypvdriverapp/api/batch-tasks/user/${userId}?jobType=NORMAL_ORDER`;
      
      // Add date filters
      if (fromDate) apiUrl += `&fromDate=${fromDate}`;
      if (toDate) apiUrl += `&toDate=${toDate}`;
      if (status) apiUrl += `&status=${status}`;
      apiUrl += `&page=${page}&size=${size}`;
      
      console.log("=".repeat(50));
      console.log("BATCH HISTORY API CALL");
      console.log("=".repeat(50));
      console.log("User ID:", userId);
      console.log("Date Range:", fromDate, "to", toDate);
      console.log("Status Filter:", status || "ALL");
      console.log("Page:", page, "Size:", size);
      console.log("API URL:", apiUrl);
      
      const response = await axios.get(apiUrl);
      
      console.log("-".repeat(50));
      console.log("RAW API RESPONSE:");
      console.log("-".repeat(50));
      console.log("Status:", response.status);
      console.log("Raw Data:", JSON.stringify(response.data, null, 2));
      console.log("-".repeat(50));
      
      const responseData = response.data;
      
      // Handle API response structure: { data: { totalTasks, userId, tasks: [...] }, success, message }
      let apiHistory = [];
      
      // Check for wrapped response: data.data.tasks (documented API structure)
      if (responseData?.data?.tasks && Array.isArray(responseData.data.tasks)) {
        console.log("Found responseData.data.tasks array with", responseData.data.tasks.length, "items");
        apiHistory = responseData.data.tasks;
      }
      // Check for direct tasks array: data.tasks
      else if (responseData?.tasks && Array.isArray(responseData.tasks)) {
        console.log("Found responseData.tasks array with", responseData.tasks.length, "items");
        apiHistory = responseData.tasks;
      }
      // Check if response itself is an array
      else if (Array.isArray(responseData)) {
        console.log("ResponseData is array with", responseData.length, "items");
        apiHistory = responseData;
      }
      // Check for data.data array
      else if (responseData?.data && Array.isArray(responseData.data)) {
        console.log("ResponseData.data is array with", responseData.data.length, "items");
        apiHistory = responseData.data;
      }
      // Check for data.content (paginated)
      else if (responseData?.content && Array.isArray(responseData.content)) {
        console.log("ResponseData.content is array with", responseData.content.length, "items");
        apiHistory = responseData.content;
      }
      // Check if data.data is a single batch object with taskId
      else if (responseData?.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
        if (responseData.data.taskId || responseData.data.id) {
          console.log("ResponseData.data is single batch object, wrapping in array");
          apiHistory = [responseData.data];
        }
      }
      // Check other common list property names
      else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
        console.log("ResponseData is object, checking for list properties...");
        console.log("Object keys:", Object.keys(responseData));
        
        // Check if data itself has batch properties (single batch object)
        if (responseData.taskId || responseData.id) {
          console.log("ResponseData itself is a batch object, wrapping in array");
          apiHistory = [responseData];
        } else {
          const possibleListKeys = ['list', 'items', 'records', 'batches', 'results'];
          for (const key of possibleListKeys) {
            if (responseData[key] && Array.isArray(responseData[key])) {
              apiHistory = responseData[key];
              console.log(`Found list in property: ${key} with ${apiHistory.length} items`);
              break;
            }
          }
        }
      }
      
      console.log("=".repeat(50));
      console.log("PARSED HISTORY:", apiHistory.length, "items");
      if (apiHistory.length > 0) {
        console.log("First item:", JSON.stringify(apiHistory[0], null, 2));
      }
      console.log("=".repeat(50));
      
      // Store pagination info from response
      if (responseData?.data) {
        setHistoryTotalTasks(responseData.data.totalTasks || apiHistory.length);
        setHistoryTotalPages(responseData.data.totalPages || 1);
      } else {
        setHistoryTotalTasks(apiHistory.length);
        setHistoryTotalPages(1);
      }
      
      // Show only API data (no localStorage merge)
      setBatchHistory(apiHistory);
      setLastHistoryUpdate(new Date());
      
    } catch (err) {
      console.error("=".repeat(50));
      console.error("BATCH HISTORY API ERROR (OFFLINE MODE):");
      console.error("=".repeat(50));
      console.error("Error:", err.message);
      console.error("Response:", err.response?.data);
      console.error("Status:", err.response?.status);
      
      // Only use localStorage when offline/API fails
      const localHistory = JSON.parse(localStorage.getItem(BATCH_HISTORY_KEY) || "[]");
      console.log("OFFLINE: Showing localStorage data with", localHistory.length, "items");
      setBatchHistory(localHistory);
      setLastHistoryUpdate(new Date());
      setError("Offline mode - showing cached data");
    } finally {
      setLoadingHistory(false);
    }
  }, [API_URL]);

  // Save batch to local storage (for offline support)
  const saveBatchToLocal = (batch) => {
    const localHistory = JSON.parse(localStorage.getItem(BATCH_HISTORY_KEY) || "[]");
    localHistory.unshift(batch); // Add to beginning
    // Keep only last 100 batches
    if (localHistory.length > 100) localHistory.pop();
    localStorage.setItem(BATCH_HISTORY_KEY, JSON.stringify(localHistory));
  };

  // Update batch in local storage
  const updateBatchInLocal = (taskId, updates) => {
    const localHistory = JSON.parse(localStorage.getItem(BATCH_HISTORY_KEY) || "[]");
    const updatedHistory = localHistory.map(b => 
      (b.taskId === taskId || b.id === taskId) ? { ...b, ...updates } : b
    );
    localStorage.setItem(BATCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  };

  // Fetch batch details - handles API response: { data: { task, successItems, failedItems, ... }, success, message }
  const fetchBatchDetails = async (taskId) => {
    console.log("========== FETCHING BATCH DETAILS ==========");
    console.log("Requested taskId:", taskId);
    
    // First check if it's a local batch
    const localHistory = JSON.parse(localStorage.getItem(BATCH_HISTORY_KEY) || "[]");
    const localBatch = localHistory.find(b => b.taskId === taskId || b.id === taskId);
    
    console.log("Local batch found:", localBatch);
    console.log("Local batch items:", localBatch?.items);
    
    // ALWAYS try API first for fresh data, don't rely on local cache
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/details`);
      console.log("========== BATCH DETAILS API RESPONSE ==========");
      console.log("RAW response.data:", JSON.stringify(response.data, null, 2));
      const responseData = response.data;
      
      // Handle documented API structure: { data: { task, successItems, failedItems, pendingItems, ... } }
      if (responseData?.data) {
        const details = responseData.data;
        console.log("details.task:", details.task);
        console.log("details.successItems:", details.successItems);
        console.log("details.failedItems:", details.failedItems);
        console.log("details.pendingItems:", details.pendingItems);
        
        // Merge task info with items
        const mergedDetails = {
          ...details.task,
          ...details,
          // Combine all items into single array for "All Items" tab
          items: [
            ...(details.successItems || []),
            ...(details.failedItems || []),
            ...(details.pendingItems || []),
            ...(details.skippedItems || [])
          ],
          successItems: details.successItems || [],
          failedItems: details.failedItems || [],
          pendingItems: details.pendingItems || [],
          skippedItems: details.skippedItems || [],
          canRetry: details.canRetry || false,
          retryableItemsCount: details.retryableItemsCount || 0,
          errorsByCategory: details.errorsByCategory || {}
        };
        console.log("========== MERGED DETAILS TO DISPLAY ==========");
        console.log("mergedDetails:", mergedDetails);
        console.log("Items to display:", mergedDetails.items);
        if (mergedDetails.items.length > 0) {
          console.log("First item full data:", JSON.stringify(mergedDetails.items[0], null, 2));
          console.log("First item status:", mergedDetails.items[0]?.status);
        }
        console.log("===============================================");
        setBatchDetails(mergedDetails);
      } else if (responseData?.items || responseData?.totalItems !== undefined) {
        console.log("Using responseData directly:", responseData);
        setBatchDetails(responseData);
      } else {
        console.log("No API data, falling back to local:", localBatch);
        setBatchDetails(localBatch || { items: [], totalItems: 0, successCount: 0, failedCount: 0 });
      }
    } catch (err) {
      console.error("Error fetching batch details:", err);
      // Fall back to local batch if available
      if (localBatch) {
        console.log("API error, using local batch:", localBatch);
        setBatchDetails(localBatch);
      } else {
        setError("Failed to load batch details: " + (err.response?.data?.message || err.message));
        setBatchDetails({ items: [], totalItems: 0, successCount: 0, failedCount: 0 });
      }
    }
  };

  // Fetch task progress (for real-time updates) - handles { data: { task, progressString, ... } }
  const fetchTaskProgress = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/progress`);
      const responseData = response.data;
      // Handle: { data: { task: {...}, progressString, status, isComplete } }
      if (responseData?.data?.task) {
        return { ...responseData.data.task, ...responseData.data };
      }
      return responseData?.data || responseData;
    } catch (err) {
      console.error("Error fetching progress:", err);
      return null;
    }
  };

  // Poll for progress updates on PROCESSING, PENDING, or QUEUED tasks
  useEffect(() => {
    let intervalId = null;
    
    if (selectedBatch && (selectedBatch.status === "PROCESSING" || selectedBatch.status === "PENDING" || selectedBatch.status === "QUEUED")) {
      intervalId = setInterval(async () => {
        const progress = await fetchTaskProgress(selectedBatch.taskId || selectedBatch.id);
        if (progress) {
          setSelectedBatch(prev => ({
            ...prev,
            ...progress,
            status: progress.status,
            successCount: progress.successCount,
            failedCount: progress.failureCount || progress.failedCount,
            skippedCount: progress.skippedCount,
            progressPercentage: progress.progressPercentage,
            currentMessage: progress.currentMessage,
            estimatedTimeRemaining: progress.estimatedTimeRemaining,
            queuePosition: progress.queuePosition
          }));
          
          // Refresh table data while processing to show real-time updates
          await fetchBatchDetails(selectedBatch.taskId || selectedBatch.id);
          
          // If completed or failed, refresh history list
          if (progress.status === "COMPLETED" || progress.status === "FAILED" || progress.status === "PARTIALLY_COMPLETED") {
            fetchBatchHistory(); // Refresh the list
          }
        }
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedBatch?.taskId, selectedBatch?.id, selectedBatch?.status]);

  // Fetch failed items for a batch - handles { data: { count, taskId, items: [...], byCategory: {...} } }
  // Each item contains: requestPayload, requestData, responsePayload, responseData
  const fetchFailedItems = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/items/failed`);
      const responseData = response.data;
      
      console.log("========== FAILED ITEMS API RESPONSE ==========");
      console.log("RAW response.data:", JSON.stringify(responseData, null, 2));
      
      const items = responseData?.data?.items || responseData?.data || responseData?.items || [];
      
      console.log("Parsed failed items:", items);
      if (items.length > 0) {
        console.log("First failed item:", JSON.stringify(items[0], null, 2));
        console.log("First item requestData:", items[0]?.requestData);
        console.log("First item requestData.genOrder:", items[0]?.requestData?.genOrder);
      }
      console.log("================================================");
      
      return items;
    } catch (err) {
      console.error("Error fetching failed items:", err);
      return [];
    }
  };

  // Fetch successful items for a batch - handles { data: { count, taskId, awbList, bkrefidList, items: [...] } }
  // Each item contains: requestPayload, requestData, responsePayload, responseData
  const fetchSuccessItems = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/items/success`);
      const responseData = response.data;
      
      console.log("========== SUCCESS ITEMS API RESPONSE ==========");
      console.log("RAW response.data:", JSON.stringify(responseData, null, 2));
      
      const items = responseData?.data?.items || responseData?.data || responseData?.items || [];
      
      console.log("Parsed success items:", items);
      if (items.length > 0) {
        console.log("First success item:", JSON.stringify(items[0], null, 2));
        console.log("First item requestData:", items[0]?.requestData);
        console.log("First item responseData:", items[0]?.responseData);
      }
      console.log("=================================================");
      
      return items;
    } catch (err) {
      console.error("Error fetching success items:", err);
      return [];
    }
  };

  // Retry single failed item - POST /api/batch-tasks/items/{itemId}/retry
  const retrySingleItem = async (itemId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/items/${itemId}/retry`);
      const responseData = response.data;
      
      if (responseData?.success) {
        const item = responseData?.data;
        if (item?.status === "SUCCESS") {
          setSuccess(`Item ${item.awbNumber} retried successfully!`);
        } else {
          setError(`Retry failed for ${item?.awbNumber}: ${item?.errorMessage || "Unknown error"}`);
        }
        // Refresh details
        if (selectedBatch) {
          await fetchBatchDetails(selectedBatch.taskId || selectedBatch.id);
          fetchBatchHistory();
        }
      } else {
        setError(responseData?.message || "Retry failed");
      }
    } catch (err) {
      console.error("Error retrying item:", err);
      setError(err.response?.data?.message || "Failed to retry item");
    } finally {
      setLoading(false);
    }
  };

  // Retry all failed items - POST /api/batch-tasks/{taskId}/retry
  const retryAllFailedItems = async (taskId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/retry`);
      const responseData = response.data;
      
      if (responseData?.success) {
        setSuccess(responseData?.message || "Retrying failed items...");
        // Start polling for progress
        if (selectedBatch) {
          setSelectedBatch(prev => ({ ...prev, status: "PROCESSING" }));
        }
        fetchBatchHistory();
      } else {
        setError(responseData?.message || "Failed to start retry");
      }
    } catch (err) {
      console.error("Error retrying all items:", err);
      setError(err.response?.data?.message || "Failed to retry items");
    } finally {
      setLoading(false);
    }
  };

  // Pause task - POST /api/batch-tasks/{taskId}/pause
  const pauseTask = async (taskId) => {
    try {
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/pause`);
      if (response.data?.success) {
        setSelectedBatch(prev => ({ ...prev, status: "PAUSED", currentMessage: "Task paused" }));
        setSuccess("Task paused");
        fetchBatchHistory();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to pause task");
    }
  };

  // Resume task - POST /api/batch-tasks/{taskId}/resume
  const resumeTask = async (taskId) => {
    try {
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/resume`);
      if (response.data?.success) {
        setSelectedBatch(prev => ({ ...prev, status: "PROCESSING", currentMessage: "Task resumed" }));
        setSuccess("Task resumed");
        fetchBatchHistory();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resume task");
    }
  };

  // Cancel task - POST /api/batch-tasks/{taskId}/cancel
  const cancelTask = async (taskId) => {
    try {
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/cancel`);
      if (response.data?.success !== false) {
        setSelectedBatch(prev => ({ ...prev, status: "CANCELLED", currentMessage: "Task cancelled" }));
        setSuccess("Task cancelled");
        setProcessingProgress(null);
        fetchBatchHistory();
      } else {
        setError(response.data?.message || "Failed to cancel task");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to cancel task");
    }
  };

  // Mark an item as resolved - POST /api/batch-tasks/items/{itemId}/resolve
  const resolveItem = async (itemId, resolutionNotes = "Manually resolved") => {
    try {
      const userId = getUserId();
      const response = await axios.post(
        `${API_URL}shypvdriverapp/api/batch-tasks/items/${itemId}/resolve?resolvedBy=${userId}&resolutionNotes=${encodeURIComponent(resolutionNotes)}`
      );
      if (response.data?.success !== false) {
        setSuccess("Item marked as resolved");
        if (selectedBatch) {
          fetchBatchDetails(selectedBatch.taskId || selectedBatch.id);
        }
      } else {
        setError(response.data?.message || "Failed to resolve item");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resolve item");
    }
  };

  // Get error summary by category - GET /api/batch-tasks/{taskId}/errors/summary
  const fetchErrorSummary = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/errors/summary`);
      return response.data?.data || [];
    } catch (err) {
      console.error("Error fetching error summary:", err);
      return [];
    }
  };

  // Get active tasks - GET /api/batch-tasks/user/{userId}/active
  const fetchActiveTasks = async () => {
    try {
      const userId = getUserId();
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/user/${userId}/active`);
      return response.data?.data?.activeTasks || [];
    } catch (err) {
      console.error("Error fetching active tasks:", err);
      return [];
    }
  };

  // Poll task progress - GET /api/batch-tasks/{taskId}/progress
  // Response: { data: { task: {...}, progressString, currentMessage, status, isComplete } }
  const pollTaskProgress = useCallback(async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${taskId}/progress`);
      const responseData = response.data;
      
      // Handle: { data: { task: {...}, progressString, status, isComplete } }
      let progressData;
      if (responseData?.data?.task) {
        progressData = {
          ...responseData.data.task,
          progressString: responseData.data.progressString || responseData.data.task.progressString,
          currentMessage: responseData.data.currentMessage || responseData.data.task.currentMessage,
          progressPercentage: responseData.data.progressPercentage || responseData.data.task.progressPercentage,
          estimatedTimeRemaining: responseData.data.estimatedTimeRemaining || responseData.data.task.estimatedTimeRemaining,
          isComplete: responseData.data.isComplete
        };
      } else if (responseData?.data) {
        progressData = responseData.data;
      } else {
        progressData = responseData;
      }
      
      setProcessingProgress(progressData);

      const status = progressData.status;
      const isComplete = progressData.isComplete;

      if (status === "COMPLETED" || (isComplete && status !== "PROCESSING")) {
        setSuccess("Batch processing completed!");
        setProcessingProgress(null);
        fetchBatchHistory();
        return true;
      } else if (status === "FAILED") {
        setError("Batch processing failed");
        setProcessingProgress(null);
        fetchBatchHistory();
        return true;
      } else if (status === "PARTIALLY_COMPLETED") {
        setSuccess("Batch processing completed with some failures");
        setProcessingProgress(null);
        fetchBatchHistory();
        return true;
      } else if (status === "CANCELLED") {
        setError("Batch processing was cancelled");
        setProcessingProgress(null);
        fetchBatchHistory();
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error polling progress:", err);
      return false;
    }
  }, [API_URL, fetchBatchHistory]);

  // Fetch partners on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const baseApiUrl = getApiBaseUrl();
        const response = await axios.get(`${baseApiUrl}/v1.0/partner/retrieve_list?page=0`);
        setPartners(response.data.data || []);
      } catch (err) {
        console.error("Error fetching partners:", err);
      }
    };
    fetchPartners();
  }, []);

  // Fetch batch history when switching to history view
  useEffect(() => {
    if (mainView === "history") {
      fetchBatchHistory();
    }
  }, [mainView, fetchBatchHistory]);

  // Fetch partner details when selected
  useEffect(() => {
    const getPartnerData = async () => {
      if (!scaninfo.partner_id) {
        setPartnerInfo(null);
        return;
      }
      const baseApiUrl = getApiBaseUrl();
      try {
        const response = await fetch(`${baseApiUrl}/v1.0/partner/retrieve?partnerId=${scaninfo.partner_id}`);
        if (!response.ok) throw new Error("Failed to fetch partner details");
        const { data } = await response.json();
        const { pname, email, ph, addr, lat, lon } = data;
        const addressParts = addr?.split(" - ") || [];
        const pkpCity = addressParts.length > 1 ? addressParts[addressParts.length - 1] : "Dubai";

        setPartnerInfo({
          pkpName: pname,
          pkpEmail: email,
          pkpMobno: ph,
          pkpAdd: addr,
          pkpCity: pkpCity,
          pkpLat: lat,
          pkpLon: lon,
        });
      } catch (err) {
        console.error("Error fetching partner:", err);
      }
    };
    getPartnerData();
  }, [scaninfo.partner_id]);

  // Helper functions for data cleaning and validation
  const cleanString = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const cleanNumber = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const num = parseFloat(String(value).replace(/[^\d.-]/g, ""));
    return isNaN(num) ? defaultVal : num;
  };

  const cleanInteger = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const num = parseInt(String(value).replace(/[^\d-]/g, ""), 10);
    return isNaN(num) ? defaultVal : num;
  };

  const cleanPhone = (value) => {
    if (!value) return "";
    // Remove all non-digit characters except +
    let phone = String(value).replace(/[^\d+]/g, "");
    // Ensure it starts with + or add it
    if (!phone.startsWith("+") && phone.length >= 9) {
      // Add UAE country code if not present
      if (!phone.startsWith("971")) {
        phone = "+971" + phone;
      } else {
        phone = "+" + phone;
      }
    }
    return phone;
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    const cleaned = cleanPhone(phone);
    // Must be at least 9 digits
    const digits = cleaned.replace(/\D/g, "");
    return digits.length >= 9 && digits.length <= 15;
  };

  const validateDate = (dateStr) => {
    if (!dateStr) return false;
    // Accept formats: DD/MM/YYYY, YYYY-MM-DD, DD-MM-YYYY
    const formats = [
      /^\d{2}\/\d{2}\/\d{4}$/,
      /^\d{4}-\d{2}-\d{2}$/,
      /^\d{2}-\d{2}-\d{4}$/
    ];
    return formats.some(f => f.test(String(dateStr).trim()));
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    const str = String(dateStr).trim();
    // If already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Convert DD/MM/YYYY or DD-MM-YYYY to YYYY-MM-DD
    const parts = str.split(/[\/\-]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return str; // Already YYYY-MM-DD
      }
      return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    }
    return str;
  };

  // Field examples for validation hints
  const fieldExamples = {
    "Ops Reference": "REF-123456 or AWB123456789",
    "Receiver's Name": "John Smith",
    "Delivery Adress": "123 Main Street, Building A, Apt 101",
    "City": "Dubai, Abu Dhabi, Sharjah, Ajman, etc.",
    "Receiver's Mobile Number": "+971501234567 or 0501234567",
    "Item Description": "Electronics, Clothing, Documents, etc.",
    "booking_date": "2026-01-20 or 20/01/2026",
    "Slot": "1, 2, 3, or 4",
    "KG Weight": "0.5, 1.0, 2.5 (in KG)",
    "noOfitems": "1, 2, 3, etc.",
    "Amount to be Recieved (AED)": "0.00, 150.50, 500.00 (COD amount)",
    "Length": "10, 20, 30 (in cm)",
    "Width": "10, 20, 30 (in cm)",
    "Height": "10, 20, 30 (in cm)",
    "Apt Details": "Apt 101, Floor 5, Building Name"
  };

  // Get example for a field
  const getFieldExample = (fieldKey) => fieldExamples[fieldKey] || "";

  // Validate a single row with comprehensive checks
  const validateRow = (row) => {
    const errors = [];
    const errorFields = [];
    const errorDetails = {}; // Store field-specific error messages with examples
    
    // Required fields
    const required = [
      { key: "Ops Reference", label: "Ops Reference" },
      { key: "Receiver's Name", label: "Receiver Name" },
      { key: "Delivery Adress", label: "Delivery Address" },
      { key: "City", label: "City" },
      { key: "Receiver's Mobile Number", label: "Receiver Phone" },
      { key: "Item Description", label: "Item Description" },
      { key: "booking_date", label: "Booking Date" },
      { key: "Slot", label: "Slot" },
      { key: "KG Weight", label: "Weight" },
      { key: "noOfitems", label: "Number of Items" },
    ];

    required.forEach(({ key, label }) => {
      const value = row[key];
      if (value === undefined || value === null || String(value).trim() === "") {
        errors.push(`${label} is required`);
        errorFields.push(key);
        errorDetails[key] = { error: `${label} is required`, example: getFieldExample(key) };
      }
    });

    // Phone validation
    const phone = row["Receiver's Mobile Number"];
    if (phone && !validatePhone(phone)) {
      errors.push("Invalid phone number format (must be 9-15 digits)");
      if (!errorFields.includes("Receiver's Mobile Number")) {
        errorFields.push("Receiver's Mobile Number");
      }
      errorDetails["Receiver's Mobile Number"] = { 
        error: "Invalid phone format", 
        example: getFieldExample("Receiver's Mobile Number") 
      };
    }

    // Date validation
    const bookingDate = row["booking_date"];
    if (bookingDate && !validateDate(bookingDate)) {
      errors.push("Invalid date format (use DD/MM/YYYY or YYYY-MM-DD)");
      if (!errorFields.includes("booking_date")) {
        errorFields.push("booking_date");
      }
      errorDetails["booking_date"] = { 
        error: "Invalid date format", 
        example: getFieldExample("booking_date") 
      };
    }

    // Numeric field validation
    const numericFields = [
      { key: "Amount to be Recieved (AED)", label: "COD Amount", min: 0 },
      { key: "KG Weight", label: "Weight", min: 0.01, max: 1000 },
      { key: "Length", label: "Length", min: 0 },
      { key: "Width", label: "Width", min: 0 },
      { key: "Height", label: "Height", min: 0 },
      { key: "noOfitems", label: "Number of Items", min: 1, max: 1000, integer: true },
      { key: "Slot", label: "Slot", min: 1, max: 4, integer: true },
    ];

    numericFields.forEach(({ key, label, min, max, integer }) => {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        const num = integer ? cleanInteger(value) : cleanNumber(value);
        if (isNaN(num)) {
          errors.push(`${label} must be a valid number`);
          if (!errorFields.includes(key)) errorFields.push(key);
          errorDetails[key] = { error: `${label} must be a number`, example: getFieldExample(key) };
        } else {
          if (min !== undefined && num < min) {
            errors.push(`${label} must be at least ${min}`);
            if (!errorFields.includes(key)) errorFields.push(key);
            errorDetails[key] = { error: `Min value: ${min}`, example: getFieldExample(key) };
          }
          if (max !== undefined && num > max) {
            errors.push(`${label} must not exceed ${max}`);
            if (!errorFields.includes(key)) errorFields.push(key);
            errorDetails[key] = { error: `Max value: ${max}`, example: getFieldExample(key) };
          }
        }
      }
    });

    // Receiver name validation (no special chars except space, dot, hyphen)
    const receiverName = row["Receiver's Name"];
    if (receiverName && !/^[a-zA-Z\s.\-']+$/.test(String(receiverName).trim())) {
      // Just a warning, not blocking
      console.warn("Receiver name contains special characters:", receiverName);
    }

    // City validation
    const city = row["City"];
    if (city) {
      const validCities = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Al Ain"];
      const cityClean = String(city).trim();
      if (!validCities.some(c => c.toLowerCase() === cityClean.toLowerCase())) {
        // Not an error, just normalize if close match
        console.log("City may need verification:", city);
      }
    }

    return { isValid: errors.length === 0, errors, errorFields, errorDetails };
  };

  // Process uploaded file
  const handleProcessFile = () => {
    if (!scaninfo.task_file || !scaninfo.task_creation_date || !scaninfo.partner_id) {
      setError("Please select date, partner and upload a file");
      return;
    }

    setError("");
    setSuccess("");
    const file = scaninfo.task_file;
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: true });

        const processedJson = rawJson.map((row) => {
          const newRow = {};
          Object.entries(row).forEach(([key, value]) => {
            if (typeof value === "number" && key.toLowerCase().includes("date")) {
              const parsedDate = XLSX.SSF.parse_date_code(value);
              if (parsedDate) {
                const jsDate = new Date(parsedDate.y, parsedDate.m - 1, parsedDate.d);
                const pad = (n) => n.toString().padStart(2, "0");
                newRow[key] = `${pad(jsDate.getDate())}/${pad(jsDate.getMonth() + 1)}/${jsDate.getFullYear()}`;
              } else {
                newRow[key] = value;
              }
            } else {
              newRow[key] = value;
            }
          });
          return newRow;
        });

        setParsedData(processedJson);

        const valid = [];
        const invalid = [];
        processedJson.forEach((row, index) => {
          const { isValid, errors, errorFields, errorDetails } = validateRow(row);
          if (isValid) {
            valid.push({ ...row, __rowId: index });
          } else {
            invalid.push({ ...row, __rowId: index, __errors: errors, __errorFields: errorFields, __errorDetails: errorDetails });
          }
        });

        setValidRows(valid);
        setInvalidRows(invalid);
        setActiveTab(invalid.length > 0 ? "invalid" : "valid");
        setSuccess(`Processed ${processedJson.length} rows: ${valid.length} valid, ${invalid.length} invalid`);
      } catch (err) {
        setError("Failed to parse file: " + err.message);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Cell editing
  const handleCellDoubleClick = (rowId, key, value) => {
    setEditingCell({ rowId, key });
    setEditValue(value || "");
  };

  const handleCellSave = (rowId, key, isValidTable) => {
    if (isValidTable) {
      setValidRows((prev) => prev.map((row) => (row.__rowId === rowId ? { ...row, [key]: editValue } : row)));
    } else {
      setInvalidRows((prev) =>
        prev.map((row) => {
          if (row.__rowId === rowId) {
            const updatedRow = { ...row, [key]: editValue };
            const result = validateRow(updatedRow);
            return { 
              ...updatedRow, 
              __errors: result.errors, 
              __errorFields: result.errorFields, 
              __errorDetails: result.errorDetails,
              __isNowValid: result.isValid 
            };
          }
          return row;
        })
      );
    }
    setEditingCell(null);
    setEditValue("");
    setDataModified(true); // Mark data as modified
  };

  const moveToValid = (rowId) => {
    const row = invalidRows.find((r) => r.__rowId === rowId);
    if (row && row.__isNowValid) {
      const { __errors, __errorFields, __isNowValid, ...cleanRow } = row;
      setValidRows((prev) => [...prev, cleanRow]);
      setInvalidRows((prev) => prev.filter((r) => r.__rowId !== rowId));
    }
  };

  // Move ALL valid rows from invalid tab to valid tab
  const moveAllValidToValid = () => {
    const validInInvalid = invalidRows.filter(row => row.__isNowValid);
    if (validInInvalid.length === 0) {
      setError("No valid rows to move. Click 'Validate All Data' first.");
      return;
    }
    
    const cleanRows = validInInvalid.map(row => {
      const { __errors, __errorFields, __isNowValid, __errorDetails, __errorCategory, ...cleanRow } = row;
      return cleanRow;
    });
    
    setValidRows(prev => [...prev, ...cleanRows]);
    setInvalidRows(prev => prev.filter(row => !row.__isNowValid));
    setSuccess(`${validInInvalid.length} valid row(s) moved to Valid tab`);
  };

  // Validate ALL data - scan all rows and mark valid/invalid
  const validateAllData = () => {
    // Combine all rows for validation
    const allRows = [...validRows, ...invalidRows];
    
    if (allRows.length === 0) {
      setError("No data to validate. Please upload a file first.");
      return;
    }
    
    const newValid = [];
    const newInvalid = [];
    
    allRows.forEach(row => {
      const { isValid, errors, errorFields, errorDetails } = validateRow(row);
      
      if (isValid) {
        // Remove error-related properties for valid rows
        const { __errors, __errorFields, __isNowValid, __errorDetails, __errorCategory, ...cleanRow } = row;
        newValid.push({ ...cleanRow, __rowId: row.__rowId, __isValid: true });
      } else {
        newInvalid.push({
          ...row,
          __isValid: false,
          __isNowValid: false,
          __errors: errors,
          __errorFields: errorFields,
          __errorDetails: errorDetails,
        });
      }
    });
    
    setValidRows(newValid);
    setInvalidRows(newInvalid);
    setDataModified(true);
    
    if (newInvalid.length > 0) {
      setActiveTab("invalid");
      setError(`Found ${newInvalid.length} row(s) with errors. Please fix them.`);
    } else {
      setActiveTab("valid");
      setSuccess(`All ${newValid.length} row(s) are valid and ready to submit!`);
    }
  };

  const generateReferences = () => {
    const timestamp = Date.now();
    setValidRows((prev) => prev.map((row, idx) => ({
      ...row,
      "Ops Reference": `REF-${timestamp}-${String(idx + 1).padStart(4, "0")}`,
    })));
    setDataModified(true); // Mark data as modified
    setSuccess("Reference IDs generated");
  };

  const downloadValidAsExcel = () => {
    if (validRows.length === 0) return;
    const exportData = validRows.map((row) => {
      const cleanRow = {};
      Object.entries(row).forEach(([key, value]) => {
        if (!key.startsWith("__")) cleanRow[key] = value;
      });
      return cleanRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Valid Data");
    XLSX.writeFile(workbook, `valid_data_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Download Invalid data as Excel
  const downloadInvalidAsExcel = () => {
    if (invalidRows.length === 0) return;
    const exportData = invalidRows.map((row) => {
      const cleanRow = {};
      Object.entries(row).forEach(([key, value]) => {
        if (!key.startsWith("__")) cleanRow[key] = value;
      });
      // Add errors column
      cleanRow["Errors"] = row.__errors?.join("; ") || row.__errorFields?.join(", ") || "Unknown error";
      return cleanRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invalid Data");
    XLSX.writeFile(workbook, `invalid_data_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Submit orders
  const handleSubmit = async () => {
    if (validRows.length === 0) {
      setError("No valid rows to submit");
      return;
    }
    if (invalidRows.length > 0) {
      setError("Please fix all invalid rows before submitting");
      return;
    }
    
    // Check for duplicate upload
    const currentHash = generateDataHash(validRows);
    if (uploadedDataHash && uploadedDataHash === currentHash) {
      setError("This data has already been uploaded. Please upload new data or make changes before submitting again.");
      return;
    }
    
    // Check if we have partner info OR if rows have stored pickup details (re-upload scenario)
    const hasRowPickupDetails = validRows.some(row => row.__pkpName || row.__pkpAdd);
    const hasPartnerIdFromRows = validRows.some(row => row.__partnerId);
    
    if (!partnerInfo && !hasRowPickupDetails) {
      setError("Partner information not loaded. Please select a partner.");
      return;
    }
    
    // Determine partner ID - from scaninfo or from first row's stored data
    const effectivePartnerId = scaninfo.partner_id || validRows[0]?.__partnerId;
    if (!effectivePartnerId) {
      setError("Partner ID is required. Please select a partner.");
      return;
    }

    setLoading(true);
    setIsUploading(true);
    setError("");
    setSuccess("");

    try {
      // Build orders array with properly cleaned and formatted data
      const orders = validRows.map((row, index) => {
        // Clean and format all fields
        const receiverPhone = cleanPhone(row["Receiver's Mobile Number"]);
        
        // Use row-level pickup details if available (re-upload), otherwise use partnerInfo
        const pkpName = row.__pkpName || partnerInfo?.pkpName || "";
        const pkpMobno = row.__pkpMobno || partnerInfo?.pkpMobno || "";
        const pkpAdd = row.__pkpAdd || partnerInfo?.pkpAdd || "";
        const pkpCity = row.__pkpCity || partnerInfo?.pkpCity || "";
        const pkpEmail = row.__pkpEmail || partnerInfo?.pkpEmail || "";
        const pkpLat = partnerInfo?.pkpLat || "";
        const pkpLon = partnerInfo?.pkpLon || "";
        
        const senderPhone = cleanPhone(pkpMobno);
        const bookingDate = formatDateForAPI(row["booking_date"]);
        const address = cleanString(row["Delivery Adress"]);
        const aptDetails = cleanString(row["Apt Details"]);
        const fullAddress = aptDetails ? `${address}, ${aptDetails}` : address;

        return {
          // Reference
          awbNumber: cleanString(row["Ops Reference"]),
          opsReference: cleanString(row["Ops Reference"]),
          
          // Sender (Partner) Info - from row or partnerInfo
          senderName: cleanString(pkpName),
          senderPhone: senderPhone,
          senderAddress: cleanString(pkpAdd),
          senderCity: cleanString(pkpCity),
          senderLat: cleanString(pkpLat),
          senderLon: cleanString(pkpLon),
          senderEmail: cleanString(pkpEmail),
          
          // Receiver Info
          receiverName: cleanString(row["Receiver's Name"]),
          receiverPhone: receiverPhone,
          receiverAddress: fullAddress,
          receiverCity: cleanString(row["City"]),
          receiverLat: "",
          receiverLon: "",
          
          // Package Details
          weight: cleanNumber(row["KG Weight"], 0.1),
          pieces: cleanInteger(row["noOfitems"], 1),
          length: cleanNumber(row["Length"], 0),
          width: cleanNumber(row["Width"], 0),
          height: cleanNumber(row["Height"], 0),
          
          // Order Details
          codAmount: cleanNumber(row["Amount to be Recieved (AED)"], 0),
          description: cleanString(row["Item Description"]),
          slot: cleanInteger(row["Slot"], 1),
          bookingDate: bookingDate,
          
          // Additional fields for compatibility
          itemWgt: cleanNumber(row["KG Weight"], 0.1),
          noOfitems: cleanInteger(row["noOfitems"], 1),
          itemLen: cleanNumber(row["Length"], 0),
          itemWid: cleanNumber(row["Width"], 0),
          itemHt: cleanNumber(row["Height"], 0),
          codAmt: cleanNumber(row["Amount to be Recieved (AED)"], 0),
          itemDesc: cleanString(row["Item Description"]),
          booking_date: bookingDate,
          
          // Row tracking
          rowIndex: index
        };
      });

      // Validate all orders have required data
      const invalidOrders = orders.filter(o => 
        !o.awbNumber || !o.receiverName || !o.receiverPhone || !o.receiverAddress || !o.receiverCity
      );
      
      if (invalidOrders.length > 0) {
        setError(`${invalidOrders.length} orders have missing required data. Please check and fix.`);
        setLoading(false);
        return;
      }

      // Build genOrders array with the correct field names for the API
      const genOrders = validRows.map((row) => {
        const receiverPhone = cleanPhone(row["Receiver's Mobile Number"]);
        
        // Use row-level pickup details if available (re-upload), otherwise use partnerInfo
        const pkpName = row.__pkpName || partnerInfo?.pkpName || "";
        const pkpMobno = row.__pkpMobno || partnerInfo?.pkpMobno || "";
        const pkpAdd = row.__pkpAdd || partnerInfo?.pkpAdd || "";
        const pkpAddApt = row.__pkpAddApt || "";
        const pkpCity = row.__pkpCity || partnerInfo?.pkpCity || "";
        const pkpEmail = row.__pkpEmail || partnerInfo?.pkpEmail || "";
        const pkpLat = partnerInfo?.pkpLat || "";
        const pkpLon = partnerInfo?.pkpLon || "";
        
        const senderPhone = cleanPhone(pkpMobno);
        const bookingDate = formatDateForAPI(row["booking_date"]);
        const address = cleanString(row["Delivery Adress"]);
        const aptDetails = cleanString(row["Apt Details"]);
        const fullAddress = aptDetails ? `${address}, ${aptDetails}` : address;

        return {
          // Reference
          OpsReference: cleanString(row["Ops Reference"]),
          
          // Receiver Info
          recvrName: cleanString(row["Receiver's Name"]),
          rcvrMobno: receiverPhone,
          rcvrEmail: cleanString(row["Receiver's Email"]) || "",
          delvAdd: cleanString(row["Delivery Adress"]),
          delvAddAptdtls: cleanString(row["Apt Details"]) || "",
          Dcity: cleanString(row["City"]),
          
          // Package Details
          itemWgt: cleanNumber(row["KG Weight"], 0.1),
          kg: cleanNumber(row["KG Weight"], 0.1),
          noitems: cleanInteger(row["noOfitems"], 1),
          ln: cleanNumber(row["Length"], 0),
          wdth: cleanNumber(row["Width"], 0),
          hiegt: cleanNumber(row["Height"], 0),
          
          // Order Details
          amntToBeRcvd: cleanNumber(row["Amount to be Recieved (AED)"], 0),
          itemDesc: cleanString(row["Item Description"]),
          slot: cleanInteger(row["Slot"], 1),
          bookingDate: bookingDate,
          serviceType: cleanString(row["Service Type"]) || "NORMAL",
        };
      });
      
      // Build pickup orders from partner info or row-level details
      const pkpGenOrders = [{
        pkpName: cleanString(validRows[0]?.__pkpName || partnerInfo?.pkpName || ""),
        pkpAdd: cleanString(validRows[0]?.__pkpAdd || partnerInfo?.pkpAdd || ""),
        pkpAddDtl: cleanString(validRows[0]?.__pkpAddApt || ""),
        pkpPhone: cleanPhone(validRows[0]?.__pkpMobno || partnerInfo?.pkpMobno || ""),
        pkpEmail: cleanString(validRows[0]?.__pkpEmail || partnerInfo?.pkpEmail || ""),
        pkpCity: cleanString(validRows[0]?.__pkpCity || partnerInfo?.pkpCity || ""),
      }];

      // Build payload in documented API format
      const payload = {
        userId: parseInt(getUserId()),
        userName: getUserName(),
        userEmail: getUserEmail(),
        jobType: "NORMAL_ORDER",
        description: `Bulk upload - ${genOrders.length} orders`,
        partnerId: parseInt(effectivePartnerId),
        sourceFileName: scaninfo.task_file?.name || "manual_upload.csv",
        orders: [
          {
            partnerId: parseInt(effectivePartnerId),
            partnerCity: cleanString(validRows[0]?.__partnerCity || partnerInfo?.pkpCity || "Dubai"),
            genOrders: genOrders,
            pkpGenOrders: pkpGenOrders
          }
        ]
      };

      console.log("Submitting payload:", JSON.stringify(payload, null, 2));

      // Use queued endpoint - batch will be processed in order by queue system
      const response = await axios.post(`${API_URL}shypvdriverapp/api/batch-tasks/create-queued`, payload);

      if (response.data) {
        const resData = response.data?.data || response.data;
        const taskId = resData.taskId || resData.id || `LOCAL-${Date.now()}`;
        
        // Log queue position info
        if (resData.queuePosition !== undefined) {
          console.log(`Batch queued at position ${resData.queuePosition}`);
        }
        
        // Save batch to local storage for history with cleaned data
        const batchRecord = {
          taskId: taskId,
          id: taskId,
          createdAt: new Date().toISOString(),
          description: `Bulk upload - ${genOrders.length} orders`,
          sourceFileName: cleanString(scaninfo.task_file?.name) || null,
          totalItems: genOrders.length,
          successCount: 0,
          failedCount: 0,
          status: resData.status || "QUEUED",
          queuePosition: resData.queuePosition,
          isQueued: resData.isQueued || true,
          partnerId: parseInt(scaninfo.partner_id),
          items: genOrders.map((order) => ({
            awbNumber: order.OpsReference,
            opsReference: order.OpsReference,
            receiverName: order.recvrName,
            receiverPhone: order.rcvrMobno,
            receiverAddress: order.delvAdd,
            receiverCity: order.Dcity,
            weight: order.itemWgt,
            pieces: order.noOfitems,
            codAmount: order.codAmt,
            description: order.itemDesc,
            bookingDate: order.booking_date,
            slot: order.slot,
            status: "PENDING"
          }))
        };
        saveBatchToLocal(batchRecord);
        
        if (resData.taskId || resData.id) {
          const queueMsg = resData.queuePosition > 1 ? ` (Queue position: ${resData.queuePosition})` : '';
          setSuccess(`Batch queued (ID: ${taskId}).${queueMsg} Processing will start automatically.`);
          // Store hash to prevent duplicate uploads
          setUploadedDataHash(currentHash);
          // Show success notification
          showNotification(`🎉 ${genOrders.length} orders queued successfully!`);
          // Start polling for progress
          const pollInterval = setInterval(async () => {
            const done = await pollTaskProgress(taskId);
            if (done) {
              clearInterval(pollInterval);
            }
          }, 2000);
          
          // No need to call /start - queue system handles processing automatically
        } else if (resData.results) {
          // Update local batch with results
          const successful = resData.results.filter(r => r.success);
          const failed = resData.results.filter(r => !r.success);
          updateBatchInLocal(taskId, {
            status: "COMPLETED",
            successCount: successful.length,
            failedCount: failed.length,
            items: resData.results
          });
          processResults(resData.results);
        } else if (resData.success !== false) {
          // Assume all succeeded
          updateBatchInLocal(taskId, {
            status: "COMPLETED",
            successCount: validRows.length,
            failedCount: 0
          });
          setSuccess("Orders submitted successfully");
          setResults({ success: validRows.length, failed: 0, items: [] });
        } else {
          updateBatchInLocal(taskId, { status: "FAILED" });
          throw new Error(resData.message || "Submission failed");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.message || err.message || "Failed to submit orders");
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const pollJobResults = async (jobId) => {
    let attempts = 0;
    const maxAttempts = 60;

    const poll = async () => {
      try {
        const response = await axios.get(`${API_URL}shypvdriverapp/api/batch-tasks/${jobId}/progress`);
        const data = response.data?.data || response.data;

        setProcessingProgress(data);

        if (data.status === "COMPLETED") {
          processResults(data.results || []);
          setProcessingProgress(null);
          return;
        } else if (data.status === "FAILED") {
          setError(data.message || "Job processing failed");
          setProcessingProgress(null);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setError("Processing timed out. Check batch history for results.");
          setProcessingProgress(null);
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setError("Failed to get results");
          setProcessingProgress(null);
        }
      }
    };
    poll();
  };

  const processResults = (resultItems) => {
    const successful = resultItems.filter((r) => r.success);
    const failed = resultItems.filter((r) => !r.success);

    setResults({
      success: successful.length,
      failed: failed.length,
      items: resultItems,
      successItems: successful,
      failedItems: failed,
    });

    if (failed.length === 0) {
      setSuccess(`All ${successful.length} orders created successfully`);
    } else {
      setSuccess(`${successful.length} orders created, ${failed.length} failed`);
    }
    setActiveTab("results");
  };

  const printLabels = (awbs) => {
    if (!awbs || awbs.length === 0) return;
    // Navigate to internal PrintLabel page with AWBs
    const awbString = awbs.join(",");
    window.location.href = `/orders/print-label?awbs=${encodeURIComponent(awbString)}`;
  };

  const handleReset = () => {
    setParsedData([]);
    setValidRows([]);
    setInvalidRows([]);
    setScaninfo({ task_file: null, task_creation_date: "", partner_id: "" });
    setError("");
    setSuccess("");
    setResults(null);
    setActiveTab("valid");
    setProcessingProgress(null);
    setDataModified(false); // Reset modified flag
    setBatchDescription("");
  };
  
  // Add manual order to valid rows
  const handleAddManualOrder = () => {
    // Validate required fields
    if (!manualOrder.receiverName || !manualOrder.receiverPhone || !manualOrder.deliveryAddress || !manualOrder.city) {
      setError("Please fill in all required fields: Receiver Name, Phone, Address, and City");
      return;
    }
    
    if (!scaninfo.partner_id) {
      setError("Please select a partner first");
      return;
    }
    
    // Generate reference if empty
    const opsRef = manualOrder.opsReference || `MAN-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    
    // Create new row in the format expected by the system
    const newRow = {
      __rowId: validRows.length + invalidRows.length + 1,
      "Ops Reference": opsRef,
      "Receiver's Name": manualOrder.receiverName,
      "Receiver's Mobile Number": manualOrder.receiverPhone,
      "Delivery Adress": manualOrder.deliveryAddress,
      "Apt Details": manualOrder.aptDetails || "",
      "City": manualOrder.city,
      "Item Description": manualOrder.itemDescription || "Package",
      "booking_date": manualOrder.bookingDate || new Date().toISOString().split('T')[0],
      "Slot": manualOrder.slot || "1",
      "KG Weight": manualOrder.weight || "0.5",
      "noOfitems": manualOrder.noOfItems || "1",
      "Length": "0",
      "Width": "0",
      "Height": "0",
      "Amount to be Recieved (AED)": manualOrder.codAmount || "0",
      "Service Type": "NORMAL",
    };
    
    // Validate the row
    const { isValid, errors, errorFields, errorDetails } = validateRow(newRow);
    
    if (isValid) {
      setValidRows(prev => [...prev, newRow]);
      setParsedData(prev => [...prev, newRow]);
      setSuccess(`Order added successfully! Reference: ${opsRef}`);
      
      // Reset manual order form
      setManualOrder({
        opsReference: "",
        receiverName: "",
        receiverPhone: "",
        deliveryAddress: "",
        aptDetails: "",
        city: "",
        weight: "0.5",
        noOfItems: "1",
        codAmount: "0",
        itemDescription: "",
        slot: "1",
        bookingDate: new Date().toISOString().split('T')[0],
      });
      
      // Auto-generate description
      if (!batchDescription) {
        setBatchDescription(`Manual entry by ${getUserName()} (ID: ${getUserId()}) - ${validRows.length + 1} order(s)`);
      } else {
        // Update count in description
        setBatchDescription(prev => prev.replace(/\d+ order\(s\)/, `${validRows.length + 1} order(s)`));
      }
    } else {
      setInvalidRows(prev => [...prev, { ...newRow, __errors: errors, __errorFields: errorFields, __errorDetails: errorDetails }]);
      setError(`Order has validation errors: ${errors.join(", ")}`);
    }
  };
  
  // Dismiss onboarding
  const dismissOnboarding = () => {
    localStorage.setItem("normalTaskOnboardingSeen", "true");
    setShowOnboarding(false);
  };

  // Download failed items as Excel for re-upload
  const downloadFailedItemsAsExcel = async (taskId) => {
    try {
      const failedItems = await fetchFailedItems(taskId);
      if (failedItems.length === 0) {
        setError("No failed items to download");
        return;
      }

      const exportData = failedItems.map((item) => ({
        "Ops Reference": item.awbNumber || item.opsReference || "",
        "Receiver's Name": item.receiverName || item.delName || "",
        "Receiver's Mobile Number": item.receiverPhone || item.delMobno || "",
        "Delivery Adress": item.receiverAddress || item.delAdd || "",
        "Apt Details": "",
        "City": item.receiverCity || item.delCity || "",
        "Item Description": item.description || item.itemDesc || "",
        "booking_date": item.bookingDate || item.booking_date || "",
        "Slot": item.slot || 1,
        "KG Weight": item.weight || item.itemWgt || 0,
        "noOfitems": item.pieces || item.noOfitems || 1,
        "Length": item.length || item.itemLen || 0,
        "Width": item.width || item.itemWid || 0,
        "Height": item.height || item.itemHt || 0,
        "Amount to be Recieved (AED)": item.codAmount || item.codAmt || 0,
        "Error Reason": item.errorMessage || item.error || "Unknown error",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Items");
      XLSX.writeFile(workbook, `failed_items_batch_${taskId}_${new Date().toISOString().split("T")[0]}.xlsx`);
      setSuccess("Failed items downloaded. Fix errors and re-upload.");
    } catch (err) {
      setError("Failed to download failed items");
    }
  };

  // View batch details in modal
  const openBatchDetails = async (batch) => {
    setSelectedBatch(batch);
    setBatchDetailTab("all");
    await fetchBatchDetails(batch.taskId || batch.id);
  };

  // Get status badge style with icon
  const getStatusBadge = (status) => {
    const statusUpper = (status || "").toUpperCase();
    switch (statusUpper) {
      case "PENDING": return styles.badgePending;
      case "QUEUED": return styles.badgeQueued;
      case "PROCESSING": return styles.badgeProcessing;
      case "COMPLETED": return styles.badgeCompleted;
      case "FAILED": return styles.badgeFailed;
      case "PAUSED": case "CANCELLED": return styles.badgePaused;
      default: return styles.badgePending;
    }
  };

  // Get status icon (SVG for professional look)
  const getStatusIcon = (status, size = 14) => {
    const statusUpper = (status || "").toUpperCase();
    switch (statusUpper) {
      case "PENDING": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
      case "QUEUED":
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="12" y2="18"/></svg>;
      case "PROCESSING": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;
      case "COMPLETED": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
      case "FAILED": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
      case "PAUSED": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="10" y1="15" x2="10" y2="9"/><line x1="14" y1="15" x2="14" y2="9"/></svg>;
      case "CANCELLED": 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>;
      default: 
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    }
  };

  // Calculate duration between two dates
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "-";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMs = end - start;
      if (diffMs < 0) return "-";
      
      const seconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      
      if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
      } else {
        return `${seconds}s`;
      }
    } catch {
      return "-";
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const renderCell = (row, key, value, isValidTable) => {
    const isEditing = editingCell?.rowId === row.__rowId && editingCell?.key === key;
    const hasError = !isValidTable && row.__errorFields?.includes(key);
    const errorDetail = !isValidTable && row.__errorDetails?.[key];
    const isDateField = key.toLowerCase().includes("date");
    const isSlotField = key === "Slot";

    if (isEditing) {
      // Date picker for date fields
      if (isDateField) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <input
              type="date"
              value={editValue ? formatDateForInput(editValue) : ""}
              onChange={(e) => {
                const date = e.target.value;
                if (date) {
                  const [y, m, d] = date.split("-");
                  setEditValue(`${d}/${m}/${y}`);
                } else {
                  setEditValue("");
                }
              }}
              onBlur={() => handleCellSave(row.__rowId, key, isValidTable)}
              autoFocus
              style={{ ...styles.input, padding: "6px 10px", fontSize: "13px" }}
            />
            {hasError && errorDetail && (
              <span style={{ fontSize: "10px", color: "#6b7280", fontStyle: "italic" }}>
                Ex: {errorDetail.example}
              </span>
            )}
          </div>
        );
      }

      // Slot dropdown
      if (isSlotField) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => handleCellSave(row.__rowId, key, isValidTable)}
              autoFocus
              style={{ ...styles.select, padding: "6px 10px", fontSize: "13px" }}
            >
              <option value="">Select Slot</option>
              <option value="1">1 - Morning (9AM-12PM)</option>
              <option value="2">2 - Afternoon (12PM-3PM)</option>
              <option value="3">3 - Evening (3PM-6PM)</option>
              <option value="4">4 - Night (6PM-9PM)</option>
            </select>
            {hasError && errorDetail && (
              <span style={{ fontSize: "10px", color: "#6b7280", fontStyle: "italic" }}>
                Ex: {errorDetail.example}
              </span>
            )}
          </div>
        );
      }

      // Default text input with example hint
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => handleCellSave(row.__rowId, key, isValidTable)}
            onKeyDown={(e) => e.key === "Enter" && handleCellSave(row.__rowId, key, isValidTable)}
            autoFocus
            style={{ ...styles.input, padding: "6px 10px", fontSize: "13px" }}
            placeholder={hasError ? getFieldExample(key) : ""}
          />
          {hasError && errorDetail && (
            <span style={{ fontSize: "10px", color: "#6b7280", fontStyle: "italic" }}>
              Ex: {errorDetail.example}
            </span>
          )}
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <span
          onDoubleClick={() => handleCellDoubleClick(row.__rowId, key, value)}
          style={{
            cursor: "pointer",
            display: "block",
            padding: "4px 6px",
            borderRadius: "4px",
            backgroundColor: hasError ? "#fee2e2" : "transparent",
          }}
          title="Double-click to edit"
        >
          {value || "-"}
        </span>
        {hasError && errorDetail && (
          <span style={{ fontSize: "10px", color: "#dc2626", padding: "0 6px" }}>
            {errorDetail.error}
          </span>
        )}
        {hasError && errorDetail && (
          <span style={{ fontSize: "10px", color: "#6b7280", fontStyle: "italic", padding: "0 6px" }}>
            Ex: {errorDetail.example}
          </span>
        )}
      </div>
    );
  };

  // Helper to format date for input type="date"
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const str = String(dateStr).trim();
    // If already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Convert DD/MM/YYYY or DD-MM-YYYY
    const parts = str.split(/[\/\-]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) return str;
      return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    }
    return "";
  };

  // Download modified data as Excel
  const downloadModifiedDataAsExcel = () => {
    const dataToExport = [...validRows, ...invalidRows].map(row => {
      const cleanRow = { ...row };
      delete cleanRow.__rowId;
      delete cleanRow.__isValid;
      delete cleanRow.__errors;
      delete cleanRow.__errorFields;
      delete cleanRow.__errorDetails;
      return cleanRow;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Modified Data");
    XLSX.writeFile(workbook, `modified_data_${new Date().toISOString().split("T")[0]}.xlsx`);
    setSuccess("Modified data downloaded successfully!");
  };

  return (
    <div style={styles.container}>
      {/* Keyframes for spin animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Header with title and toggle */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <h1 style={styles.title}>Normal Task Creation</h1>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {/* Download Modified Data - only when data has been edited */}
            {dataModified && parsedData.length > 0 && (
              <button
                style={{
                  padding: "10px 18px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#059669",
                  backgroundColor: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
                onClick={downloadModifiedDataAsExcel}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download Modified Data
              </button>
            )}
            <a
              href="/bulk_template/normal_task.csv"
              download="normal_bulk_upload.csv"
              style={{ 
                ...styles.btnSecondary, 
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              Download Template
            </a>
          </div>
        </div>
        
        {/* Toggle Switch - Below title, pushed left */}
        <div style={{ ...styles.toggleContainer, marginLeft: "0" }}>
        <button
          style={{ 
            ...styles.toggleBtn, 
            ...(mainView === "upload" ? styles.toggleBtnActive : styles.toggleBtnInactive) 
          }}
          onClick={() => setMainView("upload")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload New Batch
        </button>
        <button
          style={{ 
            ...styles.toggleBtn, 
            ...(mainView === "history" ? styles.toggleBtnActive : styles.toggleBtnInactive) 
          }}
          onClick={() => setMainView("history")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Batch History
        </button>
        </div>
      </div>

      {/* Alerts - Slide from top */}
      {error && (
        <div style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          padding: "16px 24px",
          borderRadius: "12px",
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fecaca",
          boxShadow: "0 10px 40px rgba(220, 38, 38, 0.2)",
          fontSize: "14px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          maxWidth: "600px",
          animation: "slideDownAlert 0.4s ease-out",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          {error}
          <button
            onClick={() => setError("")}
            style={{ marginLeft: "auto", padding: "4px", backgroundColor: "transparent", border: "none", cursor: "pointer", color: "#dc2626" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
      {success && (
        <div style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          padding: "16px 24px",
          borderRadius: "12px",
          backgroundColor: "#f0fdf4",
          color: "#16a34a",
          border: "1px solid #bbf7d0",
          boxShadow: "0 10px 40px rgba(22, 163, 74, 0.2)",
          fontSize: "14px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          maxWidth: "600px",
          animation: "slideDownAlert 0.4s ease-out",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {success}
          <button
            onClick={() => setSuccess("")}
            style={{ marginLeft: "auto", padding: "4px", backgroundColor: "transparent", border: "none", cursor: "pointer", color: "#16a34a" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Processing Progress */}
      {processingProgress && (
        <div style={{ ...styles.card, marginBottom: "20px" }}>
          <div style={styles.cardBody}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
              <span style={{ fontWeight: "500" }}>Processing Batch...</span>
              <span style={{ color: "#4f7df3" }}>{processingProgress.progressString || `${processingProgress.progressPercentage || 0}%`}</span>
            </div>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${processingProgress.progressPercentage || 0}%` }} />
            </div>
            {processingProgress.currentMessage && (
              <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "8px" }}>{processingProgress.currentMessage}</p>
            )}
          </div>
        </div>
      )}

      {/* UPLOAD VIEW */}
      {mainView === "upload" && (
        <>

      {/* Onboarding Card - First Time Users */}
      {showOnboarding && (
        <div style={{
          ...styles.card,
          marginBottom: "20px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden"
        }}>
          <button
            onClick={dismissOnboarding}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff"
            }}
          >
            ✕
          </button>
          <div style={{ padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div style={{ 
                width: "48px", 
                height: "48px", 
                borderRadius: "12px", 
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>Welcome to Normal Task Creation! 👋</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", opacity: 0.9 }}>Create delivery orders quickly and efficiently</p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
              <div style={{ backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "10px", padding: "16px" }}>
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>📤</div>
                <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "600" }}>Bulk Upload</h4>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>Upload Excel/CSV files with multiple orders at once</p>
              </div>
              <div style={{ backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "10px", padding: "16px" }}>
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>✏️</div>
                <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "600" }}>Manual Entry</h4>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>Add individual orders one at a time with a simple form</p>
              </div>
              <div style={{ backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "10px", padding: "16px" }}>
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>📊</div>
                <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "600" }}>Track History</h4>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>View batch history, retry failed orders, and download reports</p>
              </div>
            </div>
            <button
              onClick={dismissOnboarding}
              style={{
                marginTop: "16px",
                padding: "10px 20px",
                backgroundColor: "#fff",
                color: "#667eea",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              Got it, let's start! →
            </button>
          </div>
        </div>
      )}

      {/* Upload Card - Modern Design */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={styles.cardTitle}>Create Orders</h3>
            {/* Mode Toggle */}
            <div style={{ 
              display: "flex", 
              backgroundColor: "#f1f5f9", 
              borderRadius: "8px", 
              padding: "4px",
              gap: "4px"
            }}>
              <button
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: uploadMode === "bulk" ? "#fff" : "transparent",
                  boxShadow: uploadMode === "bulk" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  color: uploadMode === "bulk" ? "#4f7df3" : "#64748b",
                  fontWeight: "500",
                  fontSize: "13px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                onClick={() => setUploadMode("bulk")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Bulk Upload
              </button>
              <button
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: uploadMode === "manual" ? "#fff" : "transparent",
                  boxShadow: uploadMode === "manual" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  color: uploadMode === "manual" ? "#10b981" : "#64748b",
                  fontWeight: "500",
                  fontSize: "13px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                onClick={() => setUploadMode("manual")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Manual Entry
              </button>
            </div>
          </div>
        </div>
        <div style={styles.cardBody}>
          
          {/* BULK UPLOAD MODE */}
          {uploadMode === "bulk" && (
            <>
              {/* Two Column Layout: Fields Left, Upload Right */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                
                {/* LEFT SIDE - Form Fields */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  
                  {/* Date Field */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>Task Date</label>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#dc2626", backgroundColor: "#fef2f2", padding: "2px 8px", borderRadius: "4px" }}>Required</span>
                    </div>
                    <input
                      type="date"
                      style={{ ...styles.input, height: "44px" }}
                      value={scaninfo.task_creation_date}
                      onChange={(e) => setScaninfo({ ...scaninfo, task_creation_date: e.target.value })}
                    />
                  </div>
                  
                  {/* Partner Field */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>Select Partner</label>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#dc2626", backgroundColor: "#fef2f2", padding: "2px 8px", borderRadius: "4px" }}>Required</span>
                    </div>
                    <select
                      style={{ ...styles.select, height: "44px" }}
                      value={scaninfo.partner_id}
                      onChange={(e) => setScaninfo({ ...scaninfo, partner_id: e.target.value })}
                    >
                      <option value="">Select Partner</option>
                      {partners.map((p) => (
                        <option key={p.pid} value={p.pid}>{p.pname}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Description Field */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>Batch Description</label>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#059669", backgroundColor: "#ecfdf5", padding: "2px 8px", borderRadius: "4px" }}>Optional</span>
                    </div>
                    <input
                      type="text"
                      style={{ ...styles.input, height: "44px" }}
                      placeholder="e.g., Morning delivery batch"
                      value={batchDescription}
                      onChange={(e) => setBatchDescription(e.target.value)}
                    />
                  </div>
                </div>
                
                {/* RIGHT SIDE - Drag & Drop Upload (Square Box) */}
                <div
                  style={{
                    border: isDragging ? "2px dashed #4f7df3" : "2px dashed #d1d5db",
                    borderRadius: "12px",
                    backgroundColor: isDragging ? "#eff6ff" : scaninfo.task_file ? "#f0fdf4" : "#fafbfc",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    position: "relative",
                    minHeight: "200px",
                  }}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
                      setScaninfo({ ...scaninfo, task_file: file });
                      setBatchDescription(`Bulk upload by ${getUserName()} (ID: ${getUserId()})`);
                    } else {
                      setError("Please upload an Excel or CSV file only");
                    }
                  }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <input
                    id="fileInput"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      setScaninfo({ ...scaninfo, task_file: e.target.files[0] });
                      setBatchDescription(`Bulk upload by ${getUserName()} (ID: ${getUserId()})`);
                    }}
                  />
                  
                  {scaninfo.task_file ? (
                    <>
                      <div style={{ 
                        width: "56px", 
                        height: "56px", 
                        borderRadius: "12px", 
                        backgroundColor: "#d1fae5", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center" 
                      }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <path d="M9 15l2 2 4-4"/>
                        </svg>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#059669", display: "block" }}>
                          {scaninfo.task_file.name}
                        </span>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>{(scaninfo.task_file.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <span style={{ fontSize: "12px", color: "#9ca3af" }}>Click to change file</span>
                      <button
                        style={{ position: "absolute", top: "12px", right: "12px", padding: "8px", backgroundColor: "#fee2e2", border: "none", borderRadius: "8px", cursor: "pointer", display: "flex" }}
                        onClick={(e) => { e.stopPropagation(); setScaninfo({ ...scaninfo, task_file: null }); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        width: "56px", 
                        height: "56px", 
                        borderRadius: "12px", 
                        backgroundColor: "#e0e7ff", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center" 
                      }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4f7df3" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "15px", fontWeight: "600", color: "#374151", display: "block" }}>Drop file or click to browse</span>
                        <span style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px", display: "block" }}>.xlsx, .xls, .csv</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Action Buttons Row */}
              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button style={{ ...styles.btnSecondary, padding: "12px 20px" }} onClick={handleReset}>
                  Reset
                </button>
                <button 
                  style={{
                    ...styles.btnPrimary,
                    padding: "12px 24px",
                    opacity: (!scaninfo.task_file || !scaninfo.partner_id || !scaninfo.task_creation_date) ? 0.5 : 1,
                  }} 
                  onClick={handleProcessFile} 
                  disabled={loading || !scaninfo.task_file || !scaninfo.partner_id || !scaninfo.task_creation_date}
                >
                  {loading ? "Processing..." : "Process File"}
                </button>
              </div>
            </>
          )}
          
          {/* MANUAL ENTRY MODE */}
          {uploadMode === "manual" && (
            <>
              {/* Section Header */}
              <div style={{ marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #e5e7eb" }}>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Order Details</span>
              </div>
              
              {/* Row 1: Basic Info - 4 columns */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Partner *</label>
                  <select
                    style={{ ...styles.select, height: "42px" }}
                    value={scaninfo.partner_id}
                    onChange={(e) => setScaninfo({ ...scaninfo, partner_id: e.target.value })}
                  >
                    <option value="">Select Partner</option>
                    {partners.map((p) => (
                      <option key={p.pid} value={p.pid}>{p.pname}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Booking Date *</label>
                  <input
                    type="date"
                    style={{ ...styles.input, height: "42px" }}
                    value={manualOrder.bookingDate}
                    onChange={(e) => setManualOrder({ ...manualOrder, bookingDate: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Ops Reference</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="Auto-generated if empty"
                    value={manualOrder.opsReference}
                    onChange={(e) => setManualOrder({ ...manualOrder, opsReference: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Slot</label>
                  <select
                    style={{ ...styles.select, height: "42px" }}
                    value={manualOrder.slot}
                    onChange={(e) => setManualOrder({ ...manualOrder, slot: e.target.value })}
                  >
                    <option value="1">Slot 1 (Morning)</option>
                    <option value="2">Slot 2 (Afternoon)</option>
                    <option value="3">Slot 3 (Evening)</option>
                  </select>
                </div>
              </div>
              
              {/* Row 2: Receiver Info - 3 columns */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Receiver's Name *</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="Enter receiver name"
                    value={manualOrder.receiverName}
                    onChange={(e) => setManualOrder({ ...manualOrder, receiverName: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Receiver's Phone *</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="e.g., 0501234567"
                    value={manualOrder.receiverPhone}
                    onChange={(e) => setManualOrder({ ...manualOrder, receiverPhone: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>City *</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="e.g., Dubai"
                    value={manualOrder.city}
                    onChange={(e) => setManualOrder({ ...manualOrder, city: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Row 3: Address - 2 columns */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Delivery Address *</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="Enter full delivery address"
                    value={manualOrder.deliveryAddress}
                    onChange={(e) => setManualOrder({ ...manualOrder, deliveryAddress: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Apt/Building Details</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="e.g., Apt 101, Building A"
                    value={manualOrder.aptDetails}
                    onChange={(e) => setManualOrder({ ...manualOrder, aptDetails: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Row 4: Package Details - 5 columns */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Weight (KG)</label>
                  <input
                    type="number"
                    step="0.1"
                    style={{ ...styles.input, height: "42px" }}
                    value={manualOrder.weight}
                    onChange={(e) => setManualOrder({ ...manualOrder, weight: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>No. of Items</label>
                  <input
                    type="number"
                    min="1"
                    style={{ ...styles.input, height: "42px" }}
                    value={manualOrder.noOfItems}
                    onChange={(e) => setManualOrder({ ...manualOrder, noOfItems: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>COD Amount (AED)</label>
                  <input
                    type="number"
                    step="0.01"
                    style={{ ...styles.input, height: "42px" }}
                    value={manualOrder.codAmount}
                    onChange={(e) => setManualOrder({ ...manualOrder, codAmount: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Item Description</label>
                  <input
                    type="text"
                    style={{ ...styles.input, height: "42px" }}
                    placeholder="e.g., Electronics, Clothing"
                    value={manualOrder.itemDescription}
                    onChange={(e) => setManualOrder({ ...manualOrder, itemDescription: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Divider */}
              <div style={{ borderTop: "1px solid #e5e7eb", margin: "20px 0", paddingTop: "20px" }}>
                {/* Batch Description & Add Button */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "20px", alignItems: "end" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Batch Description
                      <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "400", marginLeft: "8px", textTransform: "none" }}>(Auto-generated)</span>
                    </label>
                    <input
                      type="text"
                      style={{ ...styles.input, height: "42px" }}
                      placeholder="Will be generated when you add orders"
                      value={batchDescription}
                      onChange={(e) => setBatchDescription(e.target.value)}
                    />
                  </div>
                  <button 
                    style={{
                      ...styles.btnSuccess,
                      padding: "12px 28px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      height: "42px",
                      fontWeight: "600"
                    }} 
                    onClick={handleAddManualOrder}
                    disabled={!scaninfo.partner_id}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Order
                  </button>
                </div>
              </div>
              
              {/* Orders Added Counter */}
              {validRows.length > 0 && (
                <div style={{ 
                  marginTop: "16px", 
                  padding: "12px 16px", 
                  backgroundColor: "#f0fdf4", 
                  borderRadius: "8px",
                  border: "1px solid #d1fae5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#065f46" }}>{validRows.length} order(s) added</span>
                  </div>
                  <button
                    style={{ ...styles.btnSecondary, padding: "6px 12px", fontSize: "12px" }}
                    onClick={() => setActiveTab("valid")}
                  >
                    View Orders →
                  </button>
                </div>
              )}
            </>
          )}
          
        </div>
      </div>

      {/* Stats Cards - Only show after processing */}
      {(parsedData.length > 0 || validRows.length > 0) && (
        <>
        <div style={styles.statsGrid}>
          <div style={{ ...styles.statCard, borderLeft: "4px solid #4f7df3" }}>
            <div style={{ ...styles.statValue, color: "#4f7df3" }}>{parsedData.length || validRows.length + invalidRows.length}</div>
            <div style={styles.statLabel}>Total Rows</div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: "4px solid #10b981" }}>
            <div style={{ ...styles.statValue, color: "#10b981" }}>{validRows.length}</div>
            <div style={styles.statLabel}>Valid</div>
          </div>
          <div style={{ ...styles.statCard, borderLeft: "4px solid #ef4444" }}>
            <div style={{ ...styles.statValue, color: "#ef4444" }}>{invalidRows.length}</div>
            <div style={styles.statLabel}>Invalid</div>
          </div>
        </div>

        {/* Data Card */}
        <div style={styles.card}>
        <div style={styles.cardBody}>
          {/* Tabs */}
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(activeTab === "valid" ? styles.tabActive : styles.tabInactive) }}
              onClick={() => setActiveTab("valid")}
            >
              Valid ({validRows.length})
            </button>
            <button
              style={{ ...styles.tab, ...(activeTab === "invalid" ? styles.tabActive : styles.tabInactive) }}
              onClick={() => setActiveTab("invalid")}
            >
              Invalid ({invalidRows.length})
            </button>
            {results && (
              <button
                style={{ ...styles.tab, ...(activeTab === "results" ? styles.tabActive : styles.tabInactive) }}
                onClick={() => setActiveTab("results")}
              >
                Results
              </button>
            )}
          </div>

          {/* Valid Tab */}
          {activeTab === "valid" && (
            <>
              {/* Action Buttons Row - Right aligned */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                    {validRows.length > 0 ? `Showing ${validRowsPage * validRowsPageSize + 1}-${Math.min((validRowsPage + 1) * validRowsPageSize, validRows.length)} of ${validRows.length} orders` : "Upload a file to get started"}
                  </p>
                  {/* View Mode Toggle */}
                  <div style={{ 
                    display: "flex", 
                    backgroundColor: "#f1f5f9", 
                    borderRadius: "8px", 
                    padding: "4px",
                    gap: "4px"
                  }}>
                    <button
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: viewMode === "table" ? "#fff" : "transparent",
                        boxShadow: viewMode === "table" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        color: viewMode === "table" ? "#1e40af" : "#64748b",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                      onClick={() => setViewMode("table")}
                      title="Table View"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <line x1="3" y1="9" x2="21" y2="9"/>
                        <line x1="3" y1="15" x2="21" y2="15"/>
                        <line x1="9" y1="3" x2="9" y2="21"/>
                        <line x1="15" y1="3" x2="15" y2="21"/>
                      </svg>
                    </button>
                    <button
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: viewMode === "card" ? "#fff" : "transparent",
                        boxShadow: viewMode === "card" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        color: viewMode === "card" ? "#1e40af" : "#64748b",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                      onClick={() => setViewMode("card")}
                      title="Card View"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7"/>
                        <rect x="14" y="3" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/>
                        <rect x="3" y="14" width="7" height="7"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    style={{ 
                      ...styles.btnPrimary, 
                      ...styles.btnSmall, 
                      backgroundColor: "#0891b2",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={generateReferences}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Generate Refs
                  </button>
                  <button
                    style={{ 
                      ...styles.btnSuccess, 
                      ...styles.btnSmall,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={downloadValidAsExcel}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Excel
                  </button>
                </div>
              </div>
              
              {validRows.length > 0 ? (
                <>
                  {/* TABLE VIEW */}
                  {viewMode === "table" && (
                    <div style={{ overflowX: "auto" }}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            {Object.keys(validRows[0] || {}).filter((k) => !k.startsWith("__")).map((key) => (
                              <th key={key} style={styles.th}>{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {validRows.slice(validRowsPage * validRowsPageSize, (validRowsPage + 1) * validRowsPageSize).map((row) => (
                            <tr key={row.__rowId} style={{ backgroundColor: "#f0fdf4" }}>
                              {Object.entries(row).filter(([k]) => !k.startsWith("__")).map(([key, value]) => (
                                <td key={key} style={styles.td}>{renderCell(row, key, value, true)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* CARD VIEW */}
                  {viewMode === "card" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {validRows.slice(validRowsPage * validRowsPageSize, (validRowsPage + 1) * validRowsPageSize).map((row) => {
                        const fields = Object.entries(row).filter(([k]) => !k.startsWith("__"));
                        return (
                          <div 
                            key={row.__rowId} 
                            style={{ 
                              backgroundColor: "#f0fdf4", 
                              border: "1px solid #d1fae5",
                              borderRadius: "12px",
                              padding: "20px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                            }}
                          >
                            {/* Card Header with Row Number */}
                            <div style={{ 
                              display: "flex", 
                              justifyContent: "space-between", 
                              alignItems: "center",
                              marginBottom: "16px",
                              paddingBottom: "12px",
                              borderBottom: "1px solid #d1fae5"
                            }}>
                              <span style={{ 
                                fontSize: "14px", 
                                fontWeight: "600", 
                                color: "#065f46",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                </svg>
                                Order #{row.__rowId}
                              </span>
                              <span style={{ 
                                backgroundColor: "#10b981", 
                                color: "#fff", 
                                padding: "4px 10px", 
                                borderRadius: "12px", 
                                fontSize: "11px",
                                fontWeight: "500"
                              }}>
                                Valid
                              </span>
                            </div>
                            
                            {/* Form-like Grid - 3 fields per row */}
                            <div style={{ 
                              display: "grid", 
                              gridTemplateColumns: "repeat(3, 1fr)", 
                              gap: "16px"
                            }}>
                              {fields.map(([key, value]) => (
                                <div key={key} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                  <label style={{ 
                                    fontSize: "11px", 
                                    fontWeight: "600", 
                                    color: "#6b7280",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px"
                                  }}>
                                    {key}
                                  </label>
                                  <div style={{ 
                                    backgroundColor: "#fff", 
                                    padding: "10px 12px", 
                                    borderRadius: "8px",
                                    border: "1px solid #e5e7eb",
                                    fontSize: "13px",
                                    color: "#1f2937",
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center"
                                  }}>
                                    {renderCell(row, key, value, true)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Pagination */}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    marginTop: "20px",
                    paddingTop: "16px",
                    borderTop: "1px solid #e5e7eb"
                  }}>
                    {/* Left: Pagination Info */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        Page {validRowsPage + 1} of {Math.ceil(validRows.length / validRowsPageSize) || 1}
                      </span>
                      <select
                        value={validRowsPageSize}
                        onChange={(e) => {
                          setValidRowsPageSize(Number(e.target.value));
                          setValidRowsPage(0);
                        }}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          fontSize: "13px",
                          backgroundColor: "#fff",
                          cursor: "pointer"
                        }}
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    
                    {/* Center: Navigation Buttons */}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setValidRowsPage(0)}
                        disabled={validRowsPage === 0}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: validRowsPage === 0 ? "#f3f4f6" : "#fff",
                          cursor: validRowsPage === 0 ? "not-allowed" : "pointer",
                          opacity: validRowsPage === 0 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        ⟪ First
                      </button>
                      <button
                        onClick={() => setValidRowsPage(p => Math.max(0, p - 1))}
                        disabled={validRowsPage === 0}
                        style={{
                          padding: "8px 16px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: validRowsPage === 0 ? "#f3f4f6" : "#fff",
                          cursor: validRowsPage === 0 ? "not-allowed" : "pointer",
                          opacity: validRowsPage === 0 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        ← Prev
                      </button>
                      <button
                        onClick={() => setValidRowsPage(p => Math.min(Math.ceil(validRows.length / validRowsPageSize) - 1, p + 1))}
                        disabled={validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1}
                        style={{
                          padding: "8px 16px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? "#f3f4f6" : "#fff",
                          cursor: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? "not-allowed" : "pointer",
                          opacity: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        Next →
                      </button>
                      <button
                        onClick={() => setValidRowsPage(Math.ceil(validRows.length / validRowsPageSize) - 1)}
                        disabled={validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? "#f3f4f6" : "#fff",
                          cursor: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? "not-allowed" : "pointer",
                          opacity: validRowsPage >= Math.ceil(validRows.length / validRowsPageSize) - 1 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        Last ⟫
                      </button>
                    </div>
                    
                    {/* Right: Upload Button */}
                    {invalidRows.length === 0 && !results && (
                      <button
                        style={{
                          ...styles.btnSuccess,
                          padding: "10px 24px",
                          fontSize: "14px",
                          opacity: (loading || isUploading) ? 0.85 : 1,
                          cursor: (loading || isUploading) ? "not-allowed" : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          minWidth: "160px",
                          transition: "all 0.3s ease",
                          transform: (loading || isUploading) ? "scale(0.98)" : "scale(1)",
                          ...(isUploading ? styles.btnUploading : {}),
                        }}
                        onClick={handleSubmit}
                        disabled={loading || isUploading}
                      >
                        {(loading || isUploading) ? (
                          <>
                            <div style={styles.spinner}></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/>
                              <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <span>Upload Orders</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "50px 20px", color: "#6b7280" }}>
                  <p>No valid rows. Upload and process a file to get started.</p>
                </div>
              )}
            </>
          )}

          {/* Invalid Tab */}
          {activeTab === "invalid" && (
            <>
              {/* Action Buttons Row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                    {invalidRows.length > 0 ? `Showing ${invalidRowsPage * invalidRowsPageSize + 1}-${Math.min((invalidRowsPage + 1) * invalidRowsPageSize, invalidRows.length)} of ${invalidRows.length} items` : "No invalid items"}
                  </p>
                  {/* View Mode Toggle */}
                  <div style={{ 
                    display: "flex", 
                    backgroundColor: "#f1f5f9", 
                    borderRadius: "8px", 
                    padding: "4px",
                    gap: "4px"
                  }}>
                    <button
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: invalidViewMode === "table" ? "#fff" : "transparent",
                        boxShadow: invalidViewMode === "table" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        color: invalidViewMode === "table" ? "#dc2626" : "#64748b",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                      onClick={() => setInvalidViewMode("table")}
                      title="Table View"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <line x1="3" y1="9" x2="21" y2="9"/>
                        <line x1="3" y1="15" x2="21" y2="15"/>
                        <line x1="9" y1="3" x2="9" y2="21"/>
                        <line x1="15" y1="3" x2="15" y2="21"/>
                      </svg>
                    </button>
                    <button
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: invalidViewMode === "card" ? "#fff" : "transparent",
                        boxShadow: invalidViewMode === "card" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        color: invalidViewMode === "card" ? "#dc2626" : "#64748b",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                      onClick={() => setInvalidViewMode("card")}
                      title="Card View"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7"/>
                        <rect x="14" y="3" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/>
                        <rect x="3" y="14" width="7" height="7"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    style={{ 
                      ...styles.btnPrimary, 
                      ...styles.btnSmall, 
                      backgroundColor: "#3b82f6",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={validateAllData}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 11l3 3L22 4"/>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                    </svg>
                    Validate All
                  </button>
                  {invalidRows.some(row => row.__isNowValid) && (
                    <button
                      style={{ 
                        ...styles.btnSuccess, 
                        ...styles.btnSmall,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      onClick={moveAllValidToValid}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14"/>
                        <path d="M12 5l7 7-7 7"/>
                      </svg>
                      Move Valid ({invalidRows.filter(r => r.__isNowValid).length})
                    </button>
                  )}
                  <button
                    style={{ 
                      ...styles.btnSmall,
                      backgroundColor: "#f59e0b",
                      color: "#fff",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={downloadInvalidAsExcel}
                    disabled={invalidRows.length === 0}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Excel
                  </button>
                </div>
              </div>
              
              {invalidRows.length > 0 ? (
                <>
                  {/* TABLE VIEW */}
                  {invalidViewMode === "table" && (
                    <div style={{ overflowX: "auto" }}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ ...styles.th, width: "50px" }}>#</th>
                            {Object.keys(invalidRows[0] || {}).filter((k) => !k.startsWith("__")).map((key) => (
                              <th key={key} style={styles.th}>{key}</th>
                            ))}
                            <th style={{ ...styles.th, minWidth: "200px", backgroundColor: "#fef2f2" }}>Errors</th>
                            <th style={{ ...styles.th, width: "80px" }}>Status</th>
                            <th style={{ ...styles.th, width: "100px" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invalidRows.slice(invalidRowsPage * invalidRowsPageSize, (invalidRowsPage + 1) * invalidRowsPageSize).map((row) => (
                            <tr key={row.__rowId} style={{ backgroundColor: row.__isNowValid ? "#f0fdf4" : "#fef2f2" }}>
                              <td style={{ ...styles.td, fontWeight: "600", color: "#6b7280" }}>{row.__rowId}</td>
                              {Object.entries(row).filter(([k]) => !k.startsWith("__")).map(([key, value]) => (
                                <td key={key} style={styles.td}>{renderCell(row, key, value, false)}</td>
                              ))}
                              <td style={{ ...styles.td, backgroundColor: row.__isNowValid ? "#dcfce7" : "#fee2e2", fontSize: "11px" }}>
                                {row.__isNowValid ? (
                                  <span style={{ color: "#10b981", fontWeight: "600" }}>✓ Valid</span>
                                ) : (
                                  row.__errors?.slice(0, 2).join("; ") || "Error"
                                )}
                              </td>
                              <td style={styles.td}>
                                {row.__isNowValid ? (
                                  <span style={{ color: "#10b981", fontSize: "11px", fontWeight: "600" }}>✓</span>
                                ) : (
                                  <span style={{ color: "#ef4444", fontSize: "11px" }}>{row.__errors?.length || 1} err</span>
                                )}
                              </td>
                              <td style={styles.td}>
                                {row.__isNowValid ? (
                                  <button style={{ ...styles.btnSuccess, ...styles.btnSmall, fontSize: "10px", padding: "4px 8px" }} onClick={() => moveToValid(row.__rowId)}>Move →</button>
                                ) : (
                                  <button
                                    style={{ ...styles.btnSmall, fontSize: "10px", padding: "4px 8px", backgroundColor: "#f3f4f6", border: "1px solid #d1d5db", color: "#6b7280", cursor: "pointer" }}
                                    onClick={() => {
                                      const { isValid, errors, errorFields, errorDetails } = validateRow(row);
                                      setInvalidRows(prev => prev.map(r => r.__rowId === row.__rowId ? { ...r, __isNowValid: isValid, __errors: errors, __errorFields: errorFields, __errorDetails: errorDetails } : r));
                                      if (isValid) setSuccess(`Row ${row.__rowId} is now valid!`);
                                    }}
                                  >
                                    Check
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* CARD VIEW */}
                  {invalidViewMode === "card" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {invalidRows.slice(invalidRowsPage * invalidRowsPageSize, (invalidRowsPage + 1) * invalidRowsPageSize).map((row) => {
                        const fields = Object.entries(row).filter(([k]) => !k.startsWith("__"));
                        const errorFieldNames = row.__errorFields || [];
                        return (
                          <div 
                            key={row.__rowId} 
                            style={{ 
                              backgroundColor: row.__isNowValid ? "#f0fdf4" : "#fef2f2", 
                              border: `1px solid ${row.__isNowValid ? "#d1fae5" : "#fecaca"}`,
                              borderRadius: "12px",
                              padding: "20px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                            }}
                          >
                            {/* Card Header */}
                            <div style={{ 
                              display: "flex", 
                              justifyContent: "space-between", 
                              alignItems: "center",
                              marginBottom: "16px",
                              paddingBottom: "12px",
                              borderBottom: `1px solid ${row.__isNowValid ? "#d1fae5" : "#fecaca"}`
                            }}>
                              <span style={{ 
                                fontSize: "14px", 
                                fontWeight: "600", 
                                color: row.__isNowValid ? "#065f46" : "#991b1b",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                </svg>
                                Order #{row.__rowId}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {row.__isNowValid ? (
                                  <>
                                    <span style={{ backgroundColor: "#10b981", color: "#fff", padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" }}>Valid Now</span>
                                    <button style={{ ...styles.btnSuccess, ...styles.btnSmall, fontSize: "11px" }} onClick={() => moveToValid(row.__rowId)}>Move →</button>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ backgroundColor: "#ef4444", color: "#fff", padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" }}>{row.__errors?.length || 1} Error(s)</span>
                                    <button
                                      style={{ ...styles.btnSmall, fontSize: "11px", backgroundColor: "#fff", border: "1px solid #d1d5db", color: "#6b7280", cursor: "pointer" }}
                                      onClick={() => {
                                        const { isValid, errors, errorFields, errorDetails } = validateRow(row);
                                        setInvalidRows(prev => prev.map(r => r.__rowId === row.__rowId ? { ...r, __isNowValid: isValid, __errors: errors, __errorFields: errorFields, __errorDetails: errorDetails } : r));
                                        if (isValid) setSuccess(`Row ${row.__rowId} is now valid!`);
                                      }}
                                    >
                                      Re-check
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {/* Form-like Grid - 3 fields per row */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                              {fields.map(([key, value]) => {
                                const hasError = errorFieldNames.includes(key);
                                return (
                                  <div key={key} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                    <label style={{ 
                                      fontSize: "11px", 
                                      fontWeight: "600", 
                                      color: hasError ? "#dc2626" : "#6b7280",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px"
                                    }}>
                                      {hasError && <span style={{ color: "#ef4444" }}>⚠</span>}
                                      {key}
                                    </label>
                                    <div style={{ 
                                      backgroundColor: "#fff", 
                                      padding: "10px 12px", 
                                      borderRadius: "8px",
                                      border: `1px solid ${hasError ? "#fca5a5" : "#e5e7eb"}`,
                                      fontSize: "13px",
                                      color: hasError ? "#dc2626" : "#1f2937",
                                      minHeight: "40px",
                                      display: "flex",
                                      alignItems: "center"
                                    }}>
                                      {renderCell(row, key, value, false)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Error Details */}
                            {!row.__isNowValid && row.__errors?.length > 0 && (
                              <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #fca5a5" }}>
                                <div style={{ fontSize: "11px", fontWeight: "600", color: "#dc2626", marginBottom: "8px" }}>Errors:</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                  {row.__errors.map((err, idx) => (
                                    <span key={idx} style={{ fontSize: "11px", backgroundColor: "#fee2e2", color: "#991b1b", padding: "4px 8px", borderRadius: "4px" }}>• {err}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Pagination */}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    marginTop: "20px",
                    paddingTop: "16px",
                    borderTop: "1px solid #e5e7eb"
                  }}>
                    {/* Left: Pagination Info */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        Page {invalidRowsPage + 1} of {Math.ceil(invalidRows.length / invalidRowsPageSize) || 1}
                      </span>
                      <select
                        value={invalidRowsPageSize}
                        onChange={(e) => {
                          setInvalidRowsPageSize(Number(e.target.value));
                          setInvalidRowsPage(0);
                        }}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          fontSize: "13px",
                          backgroundColor: "#fff",
                          cursor: "pointer"
                        }}
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    
                    {/* Center: Navigation Buttons */}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setInvalidRowsPage(0)}
                        disabled={invalidRowsPage === 0}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: invalidRowsPage === 0 ? "#f3f4f6" : "#fff",
                          cursor: invalidRowsPage === 0 ? "not-allowed" : "pointer",
                          opacity: invalidRowsPage === 0 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        ⟪ First
                      </button>
                      <button
                        onClick={() => setInvalidRowsPage(p => Math.max(0, p - 1))}
                        disabled={invalidRowsPage === 0}
                        style={{
                          padding: "8px 16px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: invalidRowsPage === 0 ? "#f3f4f6" : "#fff",
                          cursor: invalidRowsPage === 0 ? "not-allowed" : "pointer",
                          opacity: invalidRowsPage === 0 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        ← Prev
                      </button>
                      <button
                        onClick={() => setInvalidRowsPage(p => Math.min(Math.ceil(invalidRows.length / invalidRowsPageSize) - 1, p + 1))}
                        disabled={invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1}
                        style={{
                          padding: "8px 16px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? "#f3f4f6" : "#fff",
                          cursor: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? "not-allowed" : "pointer",
                          opacity: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        Next →
                      </button>
                      <button
                        onClick={() => setInvalidRowsPage(Math.ceil(invalidRows.length / invalidRowsPageSize) - 1)}
                        disabled={invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? "#f3f4f6" : "#fff",
                          cursor: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? "not-allowed" : "pointer",
                          opacity: invalidRowsPage >= Math.ceil(invalidRows.length / invalidRowsPageSize) - 1 ? 0.5 : 1,
                          fontSize: "12px"
                        }}
                      >
                        Last ⟫
                      </button>
                    </div>
                    
                    {/* Right: Spacer */}
                    <div style={{ minWidth: "160px" }}></div>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "50px 20px", color: "#10b981" }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: "0 auto 16px" }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <p style={{ fontWeight: "600", fontSize: "16px" }}>All rows are valid!</p>
                  <p style={{ fontSize: "13px", color: "#6b7280" }}>Switch to the Valid tab to review and submit.</p>
                </div>
              )}
            </>
          )}

          {/* Results Tab */}
          {activeTab === "results" && results && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                <div style={{ padding: "24px", backgroundColor: "#f0fdf4", borderRadius: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: "32px", fontWeight: "700", color: "#10b981" }}>{results.success}</div>
                  <div style={{ color: "#6b7280", fontSize: "14px" }}>Successful</div>
                </div>
                <div style={{ padding: "24px", backgroundColor: "#fef2f2", borderRadius: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: "32px", fontWeight: "700", color: "#ef4444" }}>{results.failed}</div>
                  <div style={{ color: "#6b7280", fontSize: "14px" }}>Failed</div>
                </div>
              </div>
              {results.successItems?.length > 0 && (
                <>
                  <button
                    style={{ ...styles.btnPrimary, marginBottom: "16px" }}
                    onClick={() => printLabels(results.successItems.map((i) => i.shypvBkrefid || i.awbNumber).filter(Boolean))}
                  >
                    Print Labels ({results.successItems.length})
                  </button>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "12px", color: "#374151" }}>Successful Orders:</h4>
                  <div style={{ overflowX: "auto", marginBottom: "20px" }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>AWB Number</th>
                          <th style={styles.th}>Shypv Ref</th>
                          <th style={styles.th}>Receiver</th>
                          <th style={styles.th}>City</th>
                          <th style={styles.th}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.successItems.map((item, idx) => (
                          <tr key={idx} style={{ backgroundColor: "#f0fdf4" }}>
                            <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "12px" }}>{item.awbNumber || item.opsReference || "-"}</td>
                            <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "12px", color: "#6366f1" }}>{item.shypvBkrefid || "-"}</td>
                            <td style={styles.td}>{item.receiverName || item.delName || "-"}</td>
                            <td style={styles.td}>{item.receiverCity || item.city || "-"}</td>
                            <td style={styles.td}>
                              {(item.shypvBkrefid || item.awbNumber) && (
                                <button
                                  style={{
                                    padding: "4px 10px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#fff",
                                    backgroundColor: "#1e3a5f",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                  }}
                                  onClick={() => printLabels([item.shypvBkrefid || item.awbNumber])}
                                >
                                  View Label
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {results.failedItems?.length > 0 && (
                <>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "12px", color: "#374151" }}>Failed Orders:</h4>
                  <div style={{ overflowX: "auto" }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Reference</th>
                          <th style={styles.th}>Receiver</th>
                          <th style={styles.th}>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.failedItems.map((item, idx) => (
                          <tr key={idx}>
                            <td style={styles.td}>{item.opsReference || "-"}</td>
                            <td style={styles.td}>{item.receiverName || item.delName || "-"}</td>
                            <td style={{ ...styles.td, color: "#ef4444" }}>{item.errorMessage || item.error || "Unknown"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
        </>
      )}
        </>
      )}

      {/* BATCH HISTORY VIEW */}
      {mainView === "history" && (
        <div style={styles.card}>
          {/* Header Row - Title Left, Controls Right */}
          <div style={{ 
            padding: "16px 20px", 
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#fafbfc",
            borderRadius: "12px 12px 0 0"
          }}>
            {/* Main Header Row */}
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px"
            }}>
              {/* Left Side - Title & Last Updated */}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h3 style={{ ...styles.cardTitle, margin: 0, fontSize: "18px" }}>Batch History</h3>
                {lastHistoryUpdate && (
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                    Last updated: {lastHistoryUpdate.toLocaleTimeString()}
                  </span>
                )}
              </div>
              
              {/* Right Side - All Filters & Buttons in One Row */}
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "8px",
                flexWrap: "wrap",
                backgroundColor: "#fff",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb"
              }}>
                {/* From Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>From</span>
                  <input
                    type="date"
                    value={historyFromDate}
                    onChange={(e) => setHistoryFromDate(e.target.value)}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: "#374151",
                      backgroundColor: "#fff",
                      minWidth: "130px"
                    }}
                  />
                </div>
                
                {/* To Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>To</span>
                  <input
                    type="date"
                    value={historyToDate}
                    onChange={(e) => setHistoryToDate(e.target.value)}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: "#374151",
                      backgroundColor: "#fff",
                      minWidth: "130px"
                    }}
                  />
                </div>
                
                {/* Status Dropdown */}
                <select
                  value={historyStatusFilter}
                  onChange={(e) => setHistoryStatusFilter(e.target.value)}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#374151",
                    backgroundColor: "#fff",
                    minWidth: "100px",
                    cursor: "pointer"
                  }}
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="FAILED">Failed</option>
                </select>
                
                {/* Divider */}
                <div style={{ width: "1px", height: "24px", backgroundColor: "#e5e7eb" }} />
                
                {/* Action Buttons */}
                <button
                  onClick={() => {
                    setHistoryPage(0);
                    fetchBatchHistory(historyFromDate, historyToDate, historyStatusFilter, 0, historyPageSize);
                  }}
                  style={{
                    padding: "6px 14px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "background-color 0.2s"
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  Apply
                </button>
                
                <button
                  onClick={() => {
                    setHistoryFromDate(getDefaultFromDate());
                    setHistoryToDate(getDefaultToDate());
                    setHistoryStatusFilter("");
                    setHistoryPage(0);
                    fetchBatchHistory(getDefaultFromDate(), getDefaultToDate(), "", 0, historyPageSize);
                  }}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    color: "#6b7280",
                    cursor: "pointer",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "background-color 0.2s"
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                    <path d="M3 3v5h5"/>
                  </svg>
                  Reset
                </button>
                
                <button
                  onClick={() => fetchBatchHistory()}
                  disabled={loadingHistory}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: loadingHistory ? "#f3f4f6" : "#fff",
                    cursor: loadingHistory ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                    color: "#6b7280",
                    transition: "background-color 0.2s"
                  }}
                  title="Refresh batch history"
                >
                  <span style={{ 
                    display: "inline-block",
                    animation: loadingHistory ? "spin 1s linear infinite" : "none",
                  }}>↻</span>
                  {loadingHistory ? "..." : "Refresh"}
                </button>
              </div>
            </div>
            
            {/* Stats Row */}
            <div style={{ 
              display: "flex", 
              justifyContent: "flex-end", 
              marginTop: "10px",
              paddingTop: "10px",
              borderTop: "1px dashed #e5e7eb"
            }}>
              <span style={{ 
                fontSize: "12px", 
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                padding: "4px 10px",
                borderRadius: "4px"
              }}>
                📊 Showing <strong>{batchHistory.length}</strong> of <strong>{historyTotalTasks}</strong> batches
                {historyTotalPages > 1 && ` • Page ${historyPage + 1} of ${historyTotalPages}`}
              </span>
            </div>
          </div>
          
          <div style={styles.cardBody}>
            {loadingHistory ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <div style={{ 
                  display: "inline-block",
                  animation: "spin 1s linear infinite",
                  fontSize: "24px",
                  marginBottom: "8px"
                }}>↻</div>
                <p style={{ color: "#6b7280", margin: 0 }}>Loading batch history...</p>
              </div>
            ) : batchHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                No batch history found. Upload your first batch to get started.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Task ID</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Progress</th>
                      <th style={styles.th}>Results</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>Duration</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchHistory.map((batch) => {
                      const totalItems = batch.totalItems || batch.total || 0;
                      const successCount = batch.successCount || batch.success || 0;
                      const failedCount = batch.failedCount || batch.failed || 0;
                      const skippedCount = batch.skippedCount || batch.skipped || 0;
                      const progressPercent = totalItems > 0 ? Math.round(((successCount + failedCount + skippedCount) / totalItems) * 100) : 0;
                      const status = batch.status || (batch.isComplete ? "COMPLETED" : "PENDING");
                      
                      return (
                        <tr key={batch.taskId || batch.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                          {/* Task ID as badge */}
                          <td style={{ ...styles.td, maxWidth: "200px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <span style={{
                                display: "inline-block",
                                backgroundColor: "#f1f5f9",
                                color: "#475569",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontFamily: "monospace",
                                fontWeight: "600",
                                wordBreak: "break-all"
                              }}>
                                {batch.taskId || batch.id}
                              </span>
                              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                                {batch.description || batch.sourceFileName || "-"}
                              </span>
                            </div>
                          </td>
                          
                          {/* Status with icon */}
                          <td style={styles.td}>
                            <span style={{ 
                              ...styles.badge, 
                              ...getStatusBadge(status),
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px"
                            }}>
                              {getStatusIcon(status)}
                              {status}
                            </span>
                          </td>
                          
                          {/* Progress bar and count */}
                          <td style={{ ...styles.td, minWidth: "150px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <div style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                fontSize: "12px",
                                fontWeight: "500"
                              }}>
                                <span>{successCount + failedCount + skippedCount}/{totalItems}</span>
                                <span>{progressPercent}%</span>
                              </div>
                              <div style={styles.progressBar}>
                                <div style={{ 
                                  ...styles.progressFill, 
                                  width: `${progressPercent}%`,
                                  backgroundColor: failedCount > 0 ? "#f59e0b" : "#10b981"
                                }}></div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Results */}
                          <td style={styles.td}>
                            <div style={{ display: "flex", gap: "16px", fontSize: "13px" }}>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#10b981", fontWeight: "500" }} title="Success">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                {successCount}
                              </span>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: failedCount > 0 ? "#ef4444" : "#9ca3af", fontWeight: "500" }} title="Failed">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                {failedCount}
                              </span>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#6b7280", fontWeight: "500" }} title="Skipped">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>
                                {skippedCount}
                              </span>
                            </div>
                          </td>
                          
                          {/* Created date */}
                          <td style={{ ...styles.td, fontSize: "12px", color: "#6b7280" }}>
                            {formatDate(batch.createdAt || batch.createdDate)}
                          </td>
                          
                          {/* Duration */}
                          <td style={{ ...styles.td, fontSize: "12px", color: "#6b7280" }}>
                            {calculateDuration(batch.processingStartedAt, batch.processingCompletedAt)}
                          </td>
                          
                          {/* Actions */}
                          <td style={styles.td}>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <button
                                style={{ 
                                  ...styles.btnPrimary, 
                                  ...styles.btnSmall,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px"
                                }}
                                onClick={() => openBatchDetails(batch)}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                View
                              </button>
                              {failedCount > 0 && (
                                <button
                                  style={{ 
                                    ...styles.btnSecondary, 
                                    ...styles.btnSmall, 
                                    color: "#ef4444",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px"
                                  }}
                                  onClick={() => downloadFailedItemsAsExcel(batch.taskId || batch.id)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                  Failed
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {historyTotalPages > 1 && (
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center", 
                    gap: "8px", 
                    marginTop: "16px",
                    paddingTop: "16px",
                    borderTop: "1px solid #e5e7eb"
                  }}>
                    <button
                      onClick={() => {
                        const newPage = Math.max(0, historyPage - 1);
                        setHistoryPage(newPage);
                        fetchBatchHistory(historyFromDate, historyToDate, historyStatusFilter, newPage, historyPageSize);
                      }}
                      disabled={historyPage === 0}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        backgroundColor: historyPage === 0 ? "#f3f4f6" : "#fff",
                        color: historyPage === 0 ? "#9ca3af" : "#374151",
                        cursor: historyPage === 0 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: "12px", color: "#6b7280" }}>
                      Page {historyPage + 1} of {historyTotalPages}
                    </span>
                    <button
                      onClick={() => {
                        const newPage = Math.min(historyTotalPages - 1, historyPage + 1);
                        setHistoryPage(newPage);
                        fetchBatchHistory(historyFromDate, historyToDate, historyStatusFilter, newPage, historyPageSize);
                      }}
                      disabled={historyPage >= historyTotalPages - 1}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        backgroundColor: historyPage >= historyTotalPages - 1 ? "#f3f4f6" : "#fff",
                        color: historyPage >= historyTotalPages - 1 ? "#9ca3af" : "#374151",
                        cursor: historyPage >= historyTotalPages - 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch Details Modal */}
      {selectedBatch && (
        <div style={styles.modal} onClick={() => { setSelectedBatch(null); setBatchDetails(null); }}>
          <div style={{ ...styles.modalContent, maxWidth: "1100px", width: "95%" }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
                <div style={{ 
                  width: "44px", 
                  height: "44px", 
                  borderRadius: "10px", 
                  backgroundColor: "#4f7df3", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  color: "#fff"
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="9" y1="21" x2="9" y2="9"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>
                    Task Details
                  </h3>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "8px", 
                    marginTop: "4px",
                    flexWrap: "wrap"
                  }}>
                    <code style={{ 
                      padding: "2px 8px", 
                      backgroundColor: "#f1f5f9", 
                      borderRadius: "4px", 
                      fontSize: "12px",
                      fontFamily: "monospace",
                      color: "#475569"
                    }}>
                      {selectedBatch.taskId || selectedBatch.id}
                    </code>
                    <span style={{ 
                      ...styles.badge, 
                      ...getStatusBadge(selectedBatch.status),
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "11px",
                      padding: "2px 8px"
                    }}>
                      {getStatusIcon(selectedBatch.status, 12)}
                      {selectedBatch.status || "PENDING"}
                    </span>
                  </div>
                </div>
                {/* Real-time progress message for PROCESSING */}
                {(selectedBatch.status === "PROCESSING" || selectedBatch.status === "PENDING") && selectedBatch.currentMessage && (
                  <div style={{ 
                    padding: "8px 12px", 
                    backgroundColor: "#eff6ff", 
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#1d4ed8",
                    maxWidth: "250px"
                  }}>
                    <div style={{ fontWeight: "500" }}>{selectedBatch.currentMessage}</div>
                    {selectedBatch.estimatedTimeRemaining && (
                      <div style={{ marginTop: "2px", opacity: 0.8 }}>ETA: {selectedBatch.estimatedTimeRemaining}</div>
                    )}
                  </div>
                )}
              </div>
              {/* Task Control Buttons */}
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {/* Pause Button - only for PROCESSING */}
                {selectedBatch.status === "PROCESSING" && (
                  <button
                    style={{ 
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#f59e0b",
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fcd34d",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => pauseTask(selectedBatch.taskId || selectedBatch.id)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                    </svg>
                    Pause
                  </button>
                )}
                {/* Resume Button - only for PAUSED */}
                {selectedBatch.status === "PAUSED" && (
                  <button
                    style={{ 
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#16a34a",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => resumeTask(selectedBatch.taskId || selectedBatch.id)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    Resume
                  </button>
                )}
                {/* Cancel Button - for PROCESSING or PAUSED */}
                {(selectedBatch.status === "PROCESSING" || selectedBatch.status === "PAUSED" || selectedBatch.status === "PENDING") && (
                  <button
                    style={{ 
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#dc2626",
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => {
                      if (window.confirm("Are you sure you want to cancel this task? This cannot be undone.")) {
                        cancelTask(selectedBatch.taskId || selectedBatch.id);
                      }
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    Cancel
                  </button>
                )}
              <button
                style={{ 
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  backgroundColor: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                onClick={() => { setSelectedBatch(null); setBatchDetails(null); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Close
              </button>
              </div>
            </div>
            <div style={styles.modalBody}>
              {/* Task Summary Header */}
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", 
                gap: "12px", 
                marginBottom: "20px"
              }}>
                {/* Total Items */}
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#f8fafc", 
                  borderRadius: "8px", 
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Items</div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", marginTop: "4px" }}>
                    {batchDetails?.totalItems || selectedBatch.totalItems || 0}
                  </div>
                </div>
                {/* Success */}
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#f0fdf4", 
                  borderRadius: "8px", 
                  border: "1px solid #bbf7d0"
                }}>
                  <div style={{ fontSize: "11px", color: "#15803d", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Success</div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#16a34a", marginTop: "4px" }}>
                    {batchDetails?.successCount || selectedBatch.successCount || 0}
                  </div>
                </div>
                {/* Failed */}
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#fef2f2", 
                  borderRadius: "8px", 
                  border: "1px solid #fecaca"
                }}>
                  <div style={{ fontSize: "11px", color: "#b91c1c", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Failed</div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#dc2626", marginTop: "4px" }}>
                    {batchDetails?.failedCount || batchDetails?.failureCount || selectedBatch.failedCount || 0}
                  </div>
                </div>
                {/* Skipped */}
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#f8fafc", 
                  borderRadius: "8px", 
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Skipped</div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#64748b", marginTop: "4px" }}>
                    {batchDetails?.skippedCount || selectedBatch.skippedCount || 0}
                  </div>
                </div>
                {/* Duration */}
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#f8fafc", 
                  borderRadius: "8px", 
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Duration</div>
                  <div style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", marginTop: "4px" }}>
                    {calculateDuration(
                      batchDetails?.processingStartedAt || selectedBatch.processingStartedAt,
                      batchDetails?.processingCompletedAt || selectedBatch.processingCompletedAt
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Bar (for processing/pending) */}
              {(selectedBatch.status === "PROCESSING" || selectedBatch.status === "PENDING") && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "12px", color: "#64748b" }}>
                    <span>Processing...</span>
                    <span>{(selectedBatch.progressPercentage || batchDetails?.progressPercentage || 0).toFixed(1)}%</span>
                  </div>
                  <div style={{ ...styles.progressBar, height: "6px" }}>
                    <div style={{ 
                      ...styles.progressFill, 
                      width: `${selectedBatch.progressPercentage || batchDetails?.progressPercentage || 0}%`,
                      backgroundColor: "#4f7df3"
                    }}></div>
                  </div>
                </div>
              )}

              {/* Error Summary if any failures */}
              {(batchDetails?.errorSummary || selectedBatch.errorSummary) && (
                <div style={{ 
                  padding: "12px 16px", 
                  backgroundColor: "#fef2f2", 
                  borderRadius: "8px", 
                  border: "1px solid #fecaca",
                  marginBottom: "20px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span style={{ fontWeight: "600", color: "#b91c1c", fontSize: "13px" }}>Error Summary</span>
                  </div>
                  <p style={{ margin: 0, fontSize: "12px", color: "#991b1b" }}>
                    {batchDetails?.errorSummary || selectedBatch.errorSummary}
                  </p>
                </div>
              )}
              {/* Detail Tabs */}
              <div style={{ 
                display: "flex", 
                gap: "8px", 
                marginBottom: "20px",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "12px",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    style={{ 
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: batchDetailTab === "all" ? "#4f7df3" : "#f3f4f6",
                      color: batchDetailTab === "all" ? "#fff" : "#6b7280",
                    }}
                    onClick={() => setBatchDetailTab("all")}
                  >
                    All Items
                  </button>
                  <button
                    style={{ 
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: batchDetailTab === "success" ? "#10b981" : "#f3f4f6",
                      color: batchDetailTab === "success" ? "#fff" : "#6b7280",
                    }}
                    onClick={() => setBatchDetailTab("success")}
                  >
                    Successful
                  </button>
                  <button
                    style={{ 
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: batchDetailTab === "pending" ? "#f59e0b" : "#f3f4f6",
                      color: batchDetailTab === "pending" ? "#fff" : "#6b7280",
                    }}
                    onClick={() => setBatchDetailTab("pending")}
                  >
                    Pending
                  </button>
                  <button
                    style={{ 
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: batchDetailTab === "failed" ? "#ef4444" : "#f3f4f6",
                      color: batchDetailTab === "failed" ? "#fff" : "#6b7280",
                    }}
                    onClick={() => setBatchDetailTab("failed")}
                  >
                    Failed
                  </button>
                </div>
                {/* Download Pending Button */}
                {batchDetails?.items?.filter(i => i.status === "PENDING" || i.status === "IN_PROGRESS").length > 0 && (
                  <button
                    style={{ 
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: "500",
                      border: "1px solid #fcd34d",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: "#fffbeb",
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => {
                      const pendingItems = batchDetails.items.filter(i => i.status === "PENDING" || i.status === "IN_PROGRESS");
                      const exportData = pendingItems.map(item => ({
                        "AWB Number": item.awbNumber || item.opsReference || "-",
                        "Shypv Ref": item.shypvBkrefid || item.shypvRef || "-",
                        "Status": item.status || "PENDING",
                        "Receiver Name": item.receiverName || "-",
                        "Receiver Phone": item.receiverPhone || "-",
                        "City": item.receiverCity || "-",
                        "Address": item.deliveryAddress || "-"
                      }));
                      const worksheet = XLSX.utils.json_to_sheet(exportData);
                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Orders");
                      XLSX.writeFile(workbook, `pending_orders_${selectedBatch.taskId || selectedBatch.id}_${new Date().toISOString().split('T')[0]}.xlsx`);
                      setSuccess(`Downloaded ${pendingItems.length} pending orders`);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Pending ({batchDetails.items.filter(i => i.status === "PENDING" || i.status === "IN_PROGRESS").length})
                  </button>
                )}
                {/* Print Labels Button - for successful items */}
                {batchDetails?.items?.filter(i => i.status === "SUCCESS" && (i.shypvBkrefid || i.shypvRef)).length > 0 && (
                  <button
                    style={{ 
                      padding: "8px 14px",
                      fontSize: "13px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: "#1e3a5f",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => {
                      const successItems = batchDetails.items.filter(i => i.status === "SUCCESS" && (i.shypvBkrefid || i.shypvRef));
                      const awbs = successItems.map(i => i.shypvBkrefid || i.shypvRef).filter(Boolean);
                      if (awbs.length > 0) {
                        window.location.href = `/orders/print-label?awbs=${encodeURIComponent(awbs.join(","))}`;
                      }
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#2c4a6e"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#1e3a5f"}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 6 2 18 2 18 9"/>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                      <rect x="6" y="14" width="12" height="8"/>
                    </svg>
                    Print Labels ({batchDetails.items.filter(i => i.status === "SUCCESS" && (i.shypvBkrefid || i.shypvRef)).length})
                  </button>
                )}
              </div>

              {/* Items Table */}
              {batchDetails?.items?.length > 0 ? (
                <div style={{ 
                  overflowX: "auto", 
                  maxHeight: "350px", 
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px"
                }}>
                  <table style={{ ...styles.table, margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>AWB Number</th>
                        <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Shypv Ref</th>
                        <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Status</th>
                        {batchDetailTab === "failed" && (
                          <>
                            <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Error Code</th>
                            <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Error Message</th>
                            <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Action</th>
                          </>
                        )}
                        {batchDetailTab !== "failed" && (
                          <>
                            <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>Receiver</th>
                            <th style={{ ...styles.th, position: "sticky", top: 0, backgroundColor: "#f9fafb", zIndex: 1 }}>City</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {batchDetails.items
                        .filter((item) => {
                          if (batchDetailTab === "success") return item.status === "SUCCESS";
                          if (batchDetailTab === "failed") return item.status === "FAILED";
                          if (batchDetailTab === "pending") return item.status === "PENDING" || item.status === "IN_PROGRESS";
                          return true;
                        })
                        .map((item, idx) => (
                          <tr key={idx} style={{ backgroundColor: item.status === "FAILED" ? "#fef2f2" : item.status === "SUCCESS" ? "#f0fdf4" : "#fff" }}>
                            <td style={{ ...styles.td, fontWeight: "500", fontFamily: "monospace", fontSize: "12px" }}>
                              {item.awbNumber || item.opsReference || "-"}
                            </td>
                            <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "12px", color: "#6366f1" }}>
                              {item.shypvBkrefid || item.shypvRef || "-"}
                            </td>
                            <td style={styles.td}>
                              <span style={{ 
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 8px",
                                fontSize: "11px",
                                fontWeight: "500",
                                borderRadius: "10px",
                                backgroundColor: item.status === "SUCCESS" ? "#d1fae5" : item.status === "FAILED" ? "#fee2e2" : "#fef3c7",
                                color: item.status === "SUCCESS" ? "#065f46" : item.status === "FAILED" ? "#dc2626" : "#92400e",
                              }}>
                                {item.status === "SUCCESS" ? (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                ) : item.status === "FAILED" ? (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                ) : (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                                )}
                                {item.status || "PENDING"}
                              </span>
                            </td>
                            {batchDetailTab === "failed" && (
                              <>
                                <td style={{ ...styles.td, fontFamily: "monospace", fontSize: "11px", color: "#9ca3af" }}>
                                  {item.errorCode || "-"}
                                </td>
                                <td style={{ ...styles.td, color: "#ef4444", fontSize: "12px", maxWidth: "200px" }}>
                                  {item.errorMessage || "-"}
                                </td>
                                <td style={styles.td}>
                                  <button
                                    style={{
                                      padding: "4px 10px",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      color: "#4f7df3",
                                      backgroundColor: "#eff6ff",
                                      border: "1px solid #bfdbfe",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px"
                                    }}
                                    onClick={() => retrySingleItem(item.id)}
                                  >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                    </svg>
                                    Retry
                                  </button>
                                </td>
                              </>
                            )}
                            {batchDetailTab !== "failed" && (
                              <>
                                <td style={styles.td}>{item.receiverName || "-"}</td>
                                <td style={styles.td}>{item.receiverCity || "-"}</td>
                              </>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  textAlign: "center", 
                  padding: "40px", 
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px dashed #d1d5db"
                }}>
                  {batchDetails ? "No items found" : "Loading items..."}
                </div>
              )}

              {/* Action Buttons for Failed Items */}
              {(batchDetails?.failedCount > 0 || selectedBatch.failedCount > 0) && (
                <div style={{ 
                  marginTop: "20px", 
                  display: "flex", 
                  gap: "12px", 
                  justifyContent: "center",
                  flexWrap: "wrap"
                }}>
                  <button
                    style={{
                      padding: "10px 20px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#fff",
                      backgroundColor: "#4f7df3",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.2s",
                    }}
                    onClick={async () => {
                      const failedItems = await fetchFailedItems(selectedBatch.taskId || selectedBatch.id);
                      console.log("========== PREPARING RE-UPLOAD DATA ==========");
                      console.log("Failed items count:", failedItems.length);
                      console.log("Failed items for re-upload:", failedItems);
                      
                      // Print RAW PAYLOAD for each failed item BEFORE mapping
                      console.log("========== RAW PAYLOAD BEFORE MAPPING ==========");
                      failedItems.forEach((item, idx) => {
                        console.log(`\n--- ITEM ${idx + 1} RAW DATA ---`);
                        console.log("Full item object:", JSON.stringify(item, null, 2));
                        console.log("Item keys available:", Object.keys(item));
                        console.log("item.id:", item.id);
                        console.log("item.awbNumber:", item.awbNumber);
                        console.log("item.status:", item.status);
                        console.log("item.errorMessage:", item.errorMessage);
                        console.log("item.errorCategory:", item.errorCategory);
                        
                        console.log("--- FLATTENED FIELDS ---");
                        console.log("item.partnerId:", item.partnerId);
                        console.log("item.partnerCity:", item.partnerCity);
                        console.log("item.receiverName:", item.receiverName);
                        console.log("item.receiverMobile:", item.receiverMobile);
                        console.log("item.receiverEmail:", item.receiverEmail);
                        console.log("item.deliveryAddress:", item.deliveryAddress);
                        console.log("item.deliveryAddressAptDetails:", item.deliveryAddressAptDetails);
                        console.log("item.deliveryCity:", item.deliveryCity);
                        console.log("item.itemDescription:", item.itemDescription);
                        console.log("item.bookingDate:", item.bookingDate);
                        console.log("item.slot:", item.slot);
                        console.log("item.serviceType:", item.serviceType);
                        console.log("item.weightKg:", item.weightKg);
                        console.log("item.numberOfItems:", item.numberOfItems);
                        console.log("item.lengthCm:", item.lengthCm);
                        console.log("item.widthCm:", item.widthCm);
                        console.log("item.heightCm:", item.heightCm);
                        console.log("item.amountToBeReceived:", item.amountToBeReceived);
                        
                        console.log("--- PICKUP FLATTENED FIELDS ---");
                        console.log("item.pickupName:", item.pickupName);
                        console.log("item.pickupAddress:", item.pickupAddress);
                        console.log("item.pickupAddressAptDetails:", item.pickupAddressAptDetails);
                        console.log("item.pickupCity:", item.pickupCity);
                        console.log("item.pickupMobile:", item.pickupMobile);
                        console.log("item.pickupEmail:", item.pickupEmail);
                        
                        console.log("--- RETRY-READY STRUCTURE (NEW - Recommended) ---");
                        console.log("item.fullOrderForRetry:", item.fullOrderForRetry);
                        if (item.fullOrderForRetry) {
                          console.log("fullOrderForRetry.partnerId:", item.fullOrderForRetry.partnerId);
                          console.log("fullOrderForRetry.partnerCity:", item.fullOrderForRetry.partnerCity);
                          console.log("fullOrderForRetry.genOrders:", item.fullOrderForRetry.genOrders);
                          console.log("fullOrderForRetry.pkpGenOrders:", item.fullOrderForRetry.pkpGenOrders);
                        }
                        
                        console.log("--- REQUEST DATA (nested) ---");
                        console.log("item.requestData:", item.requestData);
                        console.log("item.requestData?.genOrder:", item.requestData?.genOrder);
                        console.log("item.requestData?.orderDetails:", item.requestData?.orderDetails);
                        console.log("item.requestData?.pickupDetails:", item.requestData?.pickupDetails);
                        
                        console.log("--- RESPONSE DATA ---");
                        console.log("item.responseData:", item.responseData);
                      });
                      console.log("========== END RAW PAYLOAD ==========\n");
                      
                      if (failedItems.length > 0) {
                        const mappedData = failedItems.map((item, idx) => {
                          // PRIMARY SOURCE: fullOrderForRetry.genOrders[0] - This is the ACTUAL uploaded payload
                          const retryOrder = item.fullOrderForRetry?.genOrders?.[0] || {};
                          const retryPickup = item.fullOrderForRetry?.pkpGenOrders?.[0] || {};
                          
                          // FALLBACK SOURCES: flattened fields, requestData.genOrder, requestData.orderDetails
                          const genOrder = item.requestData?.genOrder || item.requestData?.orderDetails || {};
                          const pickupDetails = item.requestData?.pickupDetails || {};
                          
                          console.log(`Item ${idx + 1} mapping sources:`, {
                            awbNumber: item.awbNumber,
                            // PRIMARY: fullOrderForRetry structure
                            fullOrderForRetry: item.fullOrderForRetry,
                            retryOrder: retryOrder,
                            retryPickup: retryPickup,
                            // SECONDARY: Flattened fields from API
                            flattenedFields: {
                              receiverName: item.receiverName,
                              deliveryAddress: item.deliveryAddress,
                              deliveryCity: item.deliveryCity,
                            },
                            // TERTIARY: genOrder fallback
                            genOrder: genOrder
                          });
                          
                          return {
                            __rowId: idx + 1,
                            __isValid: false,
                            __errors: [item.errorMessage || "Previous upload failed"],
                            __errorFields: [],
                            __errorDetails: {},
                            __errorCategory: item.errorCategory || "UNKNOWN",
                            __itemId: item.id, // Store item ID for reference
                            __partnerId: item.fullOrderForRetry?.partnerId || item.partnerId || genOrder.partnerId || item.requestData?.partnerId || "",
                            __partnerCity: item.fullOrderForRetry?.partnerCity || item.partnerCity || "",
                            __fullOrderForRetry: item.fullOrderForRetry || null, // Store for direct re-upload option
                            
                            // PRIORITY: 1. retryOrder (fullOrderForRetry.genOrders[0]) 2. flattened fields 3. genOrder fallback
                            "Ops Reference": retryOrder.OpsReference || item.awbNumber || genOrder.OpsReference || "",
                            "Receiver's Name": retryOrder.recvrName || item.receiverName || genOrder.recvrName || "",
                            "Receiver's Mobile Number": retryOrder.rcvrMobno || item.receiverMobile || genOrder.rcvrMobno || "",
                            "Receiver's Email": retryOrder.rcvrEmail || item.receiverEmail || genOrder.rcvrEmail || "",
                            "Delivery Adress": retryOrder.delvAdd || item.deliveryAddress || genOrder.delvAdd || "",
                            "Apt Details": retryOrder.delvAddAptdtls || item.deliveryAddressAptDetails || genOrder.delvAddAptdtls || "",
                            "City": retryOrder.Dcity || item.deliveryCity || genOrder.Dcity || "",
                            "Item Description": retryOrder.itemDesc || item.itemDescription || genOrder.itemDesc || "",
                            "booking_date": retryOrder.bookingDate || item.bookingDate || genOrder.bookingDate || "",
                            "Slot": retryOrder.slot || item.slot || genOrder.slot || 1,
                            "Service Type": retryOrder.serviceType || item.serviceType || genOrder.serviceType || "NORMAL",
                            "KG Weight": retryOrder.kg || item.weightKg || genOrder.kg || 0,
                            "noOfitems": retryOrder.noitems || item.numberOfItems || genOrder.noitems || 1,
                            "Length": retryOrder.ln || item.lengthCm || genOrder.ln || 0,
                            "Width": retryOrder.wdth || item.widthCm || genOrder.wdth || 0,
                            "Height": retryOrder.hiegt || item.heightCm || genOrder.hiegt || 0,
                            "Amount to be Recieved (AED)": retryOrder.amntToBeRcvd || item.amountToBeReceived || genOrder.amntToBeRcvd || 0,
                            
                            // Store pickup details - PRIORITY: 1. retryPickup 2. flattened fields 3. pickupDetails fallback
                            __pkpName: retryPickup.pkpName || item.pickupName || pickupDetails.pkp_name || pickupDetails.pkpName || "",
                            __pkpMobno: retryPickup.pkpPhone || item.pickupMobile || pickupDetails.pkp_mob_no || pickupDetails.pkpPhone || "",
                            __pkpAdd: retryPickup.pkpAdd || item.pickupAddress || pickupDetails.pkp_add || pickupDetails.pkpAdd || "",
                            __pkpAddApt: retryPickup.pkpAddDtl || item.pickupAddressAptDetails || pickupDetails.pkp_add_apt_dtls || pickupDetails.pkpAddDtl || "",
                            __pkpCity: retryPickup.pkpCity || item.pickupCity || pickupDetails.Pcity || pickupDetails.pkpCity || "",
                            __pkpEmail: retryPickup.pkpEmail || item.pickupEmail || pickupDetails.pkp_email || pickupDetails.pkpEmail || "",
                          };
                        });
                        
                        console.log("Mapped data for re-upload:", mappedData);
                        console.log("================================================");
                        
                        // Auto-select partner from the first failed item
                        const firstPartnerId = mappedData[0]?.__partnerId;
                        if (firstPartnerId) {
                          console.log("Auto-selecting partner ID:", firstPartnerId);
                          setScaninfo(prev => ({ ...prev, partner_id: String(firstPartnerId) }));
                        }
                        
                        setParsedData(mappedData);
                        setInvalidRows(mappedData);
                        setValidRows([]);
                        setActiveTab("invalid");
                        setMainView("upload");
                        setSelectedBatch(null);
                        setBatchDetails(null);
                        setSuccess(`${failedItems.length} failed items loaded for editing. Fix errors and re-upload.${firstPartnerId ? ` Partner ID ${firstPartnerId} auto-selected.` : ''}`);
                      } else {
                        setError("No failed items found to re-upload");
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Fix Errors & Re-upload
                  </button>
                  <button
                    style={{
                      padding: "10px 20px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#dc2626",
                      backgroundColor: "#fff",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.2s",
                    }}
                    onClick={() => downloadFailedItemsAsExcel(selectedBatch.taskId || selectedBatch.id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Excel
                  </button>
                  <button
                    style={{
                      padding: "10px 20px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#fff",
                      backgroundColor: "#f59e0b",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.2s",
                    }}
                    onClick={() => retryAllFailedItems(selectedBatch.taskId || selectedBatch.id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </svg>
                    Retry All Failed
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Notification Overlay - Appears from top */}
      {showSuccessNotification && (
        <div style={styles.notificationOverlay}>
          <div style={styles.notificationCard}>
            <div style={styles.notificationIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <div>
              <p style={styles.notificationText}>{notificationMessage}</p>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0" }}>This will close automatically</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
